<template>
    <div>
        <b-sidebar 
            :id="sidebarId" 
            ref="sidebarChatIA"
            v-model="sidebarVisivel"
            title="Assistente IA" 
            :width="sidebarWidth"
            right 
            shadow 
            backdrop 
            no-header 
            @hidden="fecharSidebar"
            @shown="abrirSidebar"
        >
            <template #default="{ hide }">
                <!-- Header -->
                <div class="chat-header">
                    <div class="header-info">
                        <div class="avatar-ia">
                            <i class="fa fa-comments"></i>
                        </div>
                        <div class="header-text">
                            <strong>Assistente virtual</strong>
                            <small v-if="sessaoAtiva">
                                <span class="status-dot"></span>
                                Online <span v-if="sessaoId" class="session-id">• Sessão #{{ sessaoId }}</span>
                            </small>
                            <small v-else>
                                <span class="status-dot offline"></span>
                                Conectando...
                            </small>
                        </div>
                    </div>
                    <div class="header-actions">
                        <b-dropdown
                            variant="transparent"
                            size="sm"
                            no-caret
                            right
                            class="tira-sombra"
                        >
                            <template #button-content>
                                <i class="fa fa-cogs m-0"></i>
                            </template>
                            
                            <!-- Consumo de Tokens -->
                            <b-dropdown-header v-if="quota" class="dropdown-header-custom">
                                <span class="dropdown-header-text">Consumo</span>
                            </b-dropdown-header>
                            <b-dropdown-text v-if="quota" class="quota-dropdown-info">
                                <div class="quota-mini">
                                    <span class="quota-mini-label">
                                        {{ formatNumber(quota.usuario.consumido) }}
                                        <template v-if="!quota.empresa.ilimitado">
                                            / {{ formatNumber(quota.usuario.limite || quota.empresa.cota_mensal) }}
                                        </template>
                                        <template v-else>
                                            tokens
                                        </template>
                                    </span>
                                    <div v-if="!quota.empresa.ilimitado" class="quota-mini-bar">
                                        <div 
                                            class="quota-mini-fill"
                                            :style="{ width: quota.porcentagem_usada + '%' }"
                                            :class="getQuotaClass(quota.porcentagem_usada)"
                                        ></div>
                                    </div>
                                </div>
                            </b-dropdown-text>
                            
                            <b-dropdown-divider v-if="quota"></b-dropdown-divider>
                            
                            <b-dropdown-header class="dropdown-header-custom">
                                <span class="dropdown-header-text">Configurações</span>
                            </b-dropdown-header>
                            
                            <b-dropdown-item @click="abrirBibliotecaPrompts">
                                <i class="fa fa-book mr-2"></i>
                                Biblioteca de modelos
                            </b-dropdown-item>
                            
                            <b-dropdown-item
                                v-if="!isMobile"
                                @click="toggleExpandido"
                            >
                                <i :class="chatExpandido ? 'fa fa-compress mr-2' : 'fa fa-expand mr-2'"></i>
                                {{ chatExpandido ? 'Reduzir chat' : 'Expandir chat' }}
                            </b-dropdown-item>

                            <b-dropdown-item @click="abrirConfiguracoesIa">
                                <i class="fa fa-cog mr-2"></i>
                                Configurações
                            </b-dropdown-item>

                            <b-dropdown-divider v-if="persistirSessao"></b-dropdown-divider>
                            
                            <b-dropdown-item 
                                v-if="persistirSessao" 
                                @click="limparChat"
                                :disabled="carregando"
                            >
                                <i class="fa fa-eraser mr-2"></i>
                                Limpar conversa
                            </b-dropdown-item>
                        </b-dropdown>
                        
                        <span class="header-icon-btn" @click="hide" title="Fechar">
                            <i class="fa fa-times"></i>
                        </span>
                    </div>
                </div>

                <div ref="chatMessages" class="chat-messages" @scroll="onScrollMessages">
                    <div v-if="carregandoHistorico && mensagens.length > 0" class="loading-history text-center py-2">
                        <i class="fa fa-spinner fa-spin mr-1"></i>
                        <small class="text-muted">Carregando mensagens anteriores...</small>
                    </div>
                    
                    <div v-if="temMaisHistorico && !carregandoHistorico && historicoCarregado" class="load-more-hint text-center py-2">
                        <small class="text-muted">↑ Role para cima para ver mais mensagens</small>
                    </div>
                    
                    <div 
                        v-for="(msg, index) in mensagens"
                        :key="`msg-group-${index}`"
                        class="message-group"
                    >
                        <div
                            class="message"
                            :class="[
                                msg.role === 'user' ? 'message-user' : 'message-assistant',
                                msg.isStreaming ? 'message-streaming' : ''
                            ]"
                        >
                            <div class="message-avatar">
                                <i :class="msg.role === 'user' ? 'fa fa-user' : 'fa fa-comments'"></i>
                            </div>
                        <div class="message-content">
                            <!-- Indicador de Thinking (para streaming) -->
                            <div v-if="msg.isStreaming && msg.thinking" class="thinking-indicator">
                                <i class="fa fa-cog"></i>
                                <span>{{ msg.thinking }}</span>
                            </div>
                            
                            <!-- Steps do Planner (agrupados por categoria, exceto "Analisando") -->
                            <div v-if="msg.isStreaming && msg.steps && msg.steps.length > 0" class="thinking-steps">
                                <div 
                                    v-for="(group, groupKey) in getGroupedSteps(msg.steps)" 
                                    :key="groupKey" 
                                    class="thinking-step-group"
                                >
                                    <!-- Não exibe "Analisando sua solicitação" -->
                                    <template v-if="group.category !== 'Analisando sua solicitação'">
                                        <div class="step-category">
                                            <i :class="{
                                                'fa fa-spinner fa-spin': group.status === 'running',
                                                'fa fa-check': group.status === 'done',
                                                'fa fa-times': group.status === 'error'
                                            }"></i>
                                            <span>{{ group.category }}</span>
                                        </div>
                                        <div v-if="group.lastItem" class="step-detail">
                                            <span>{{ group.lastItem }}</span>
                                        </div>
                                    </template>
                                </div>
                            </div>
                            
                            <div class="message-text" v-html="formatarMensagem(msg.content)"></div>

                            <!-- Arquivos gerados por skills (xlsx, pdf, docx, pptx, imagens) -->
                            <div v-if="msg.arquivos && msg.arquivos.length > 0" class="arquivos-anexos mt-2">
                                <a
                                    v-for="(arq, arqIdx) in msg.arquivos"
                                    :key="`${msg.id || 'msg'}-file-${arqIdx}`"
                                    :href="arq.downloadUrl"
                                    :download="arq.filename"
                                    target="_blank"
                                    rel="noopener"
                                    class="arquivo-link"
                                    :title="`Baixar ${arq.filename}`"
                                >
                                    <i :class="arq.kind === 'image' ? 'fa fa-image' : 'fa fa-file-o'"></i>
                                    <span class="arquivo-nome">{{ arq.filename }}</span>
                                    <i class="fa fa-download ml-1"></i>
                                </a>
                            </div>

                            <!-- Botões de confirmação (Human-in-the-Loop MCP) -->
                            <div v-if="msg.isConfirmation && !msg.confirmationAnswered" class="confirmation-buttons mt-2">
                                <button 
                                    class="btn btn-success btn-sm mr-2" 
                                    @click="responderConfirmacao(index, 'sim')"
                                    :disabled="carregando"
                                >
                                    <i class="fa fa-check mr-1"></i> Sim, confirmar
                                </button>
                                <button 
                                    class="btn btn-danger btn-sm" 
                                    @click="responderConfirmacao(index, 'nao')"
                                    :disabled="carregando"
                                >
                                    <i class="fa fa-times mr-1"></i> Não, cancelar
                                </button>
                            </div>
                            <div v-if="msg.isConfirmation && msg.confirmationAnswered" class="confirmation-answered mt-2">
                                <small class="text-muted">
                                    <i :class="msg.confirmationResponse === 'sim' ? 'fa fa-check text-success' : 'fa fa-times text-danger'"></i>
                                    {{ msg.confirmationResponse === 'sim' ? 'Confirmado' : 'Cancelado' }}
                                </small>
                            </div>
                            
                            <!-- Botões de seleção (desabilitados temporariamente) -->
                            <!-- <div v-if="msg.isSelection && !msg.selectionAnswered" class="selection-buttons mt-2">
                                <div class="d-flex flex-wrap gap-1">
                                    <button 
                                        v-for="opt in msg.selectionOptions" 
                                        :key="opt.index"
                                        class="btn btn-sm btn-outline-primary mr-1 mb-1"
                                        @click="responderSelecao(index, opt.index)"
                                        :disabled="carregando"
                                    >
                                        <span class="badge badge-primary mr-1">{{ opt.index }}</span>
                                        {{ opt.text }}
                                    </button>
                                </div>
                            </div>
                            <div v-if="msg.isSelection && msg.selectionAnswered" class="selection-answered mt-2">
                                <small class="text-muted">
                                    <i class="fa fa-check text-success mr-1"></i>
                                    Selecionado: {{ msg.selectionResponse === '0' ? 'Todos' : 'Opção ' + msg.selectionResponse }}
                                </small>
                            </div> -->
                            
                            <!-- Botão de ação (ex: comprar tokens) -->
                            <div v-if="msg.acao" class="message-action mt-2">
                                <a 
                                    v-if="msg.acao.tipo === 'comprar_tokens'"
                                    :href="msg.acao.url"
                                    class="btn btn-sm btn-warning"
                                    target="_blank"
                                >
                                    <i class="fa fa-shopping-cart mr-1"></i>
                                    {{ msg.acao.label || 'Comprar tokens extras' }}
                                </a>
                            </div>
                            
                            <!-- Botões de opções removidos -->
                            
                            <div 
                                v-if="msg.tools_executed" 
                                class="message-tools"
                                :class="{ 'clickable': mostrarDebug && msg.debugInfo }"
                                @click="mostrarDebug && msg.debugInfo ? abrirDebugModal(msg) : null"
                                :title="mostrarDebug && msg.debugInfo ? 'Clique para ver detalhes' : ''"
                            >
                                <small class="text-muted">
                                    <i class="fa fa-cog mr-1"></i>
                                    {{ msg.tools_executed }} ferramenta(s)
                                    <i v-if="mostrarDebug && msg.debugInfo" class="fa fa-external-link ml-1"></i>
                                </small>
                            </div>
                            
                            <!-- Botão "Gráfico disponível" (desabilitado temporariamente) -->
                            <div 
                                v-if="false && msg.chart_available && msg.chart_tool_data && !msg.chart_generated" 
                                class="chart-available-indicator mt-2"
                            >
                                <button 
                                    class="suggestion-chip"
                                    @click="gerarGrafico(msg)"
                                    :disabled="carregando"
                                >
                                    <i class="fa fa-bar-chart-o mr-1"></i>
                                    <span v-if="!msg.gerandoGrafico">Gráfico disponível</span>
                                    <span v-else><i class="fa fa-spinner fa-spin mr-1"></i> Gerando...</span>
                                </button>
                            </div>
                            
                            <!-- Barra de ações no final da mensagem -->
                            <div 
                                v-if="msg.role === 'assistant' && !msg.isStreaming" 
                                class="message-actions-bar"
                            >
                                <!-- Copiar -->
                                <button 
                                    class="action-btn"
                                    @click="copiarMensagem(msg)"
                                    title="Copiar mensagem"
                                >
                                    <i :class="msg.copiado ? 'fa fa-check' : 'fa fa-copy'"></i>
                                </button>
                                
                                <!-- Exportar PDF -->
                                <button 
                                    class="action-btn"
                                    @click="exportarPDF(msg)"
                                    title="Exportar como PDF"
                                    :disabled="msg.exportandoPdf"
                                >
                                    <i :class="msg.exportandoPdf ? 'fa fa-spinner fa-spin' : 'fa fa-print'"></i>
                                </button>
                                
                                <!-- Like -->
                                <button 
                                    class="action-btn"
                                    :class="{ active: msg.avaliacao === 'positivo' }"
                                    @click="avaliarResposta(msg, 'positivo')"
                                    title="Resposta útil"
                                    :disabled="msg.avaliacao"
                                >
                                    <i class="fa fa-thumbs-o-up"></i>
                                </button>
                                
                                <!-- Dislike -->
                                <button 
                                    class="action-btn"
                                    :class="{ active: msg.avaliacao === 'negativo' }"
                                    @click="avaliarResposta(msg, 'negativo')"
                                    title="Resposta incorreta"
                                    :disabled="msg.avaliacao"
                                >
                                    <i class="fa fa-thumbs-o-down"></i>
                                </button>
                            </div>
                            
                            <!-- Barra de ações para mensagens do usuário -->
                            <div 
                                v-if="msg.role === 'user'" 
                                class="message-actions-bar user-actions"
                            >
                                <button 
                                    class="action-btn"
                                    :class="{ active: msg.favorito }"
                                    @click="favoritarPrompt(msg)"
                                    title="Salvar nos favoritos"
                                >
                                    <i :class="msg.favorito ? 'fa fa-star' : 'fa fa-star-o'"></i>
                                </button>
                            </div>
                        </div>
                    </div>
                    
                        <!-- Container dos gráficos (fora do balão da mensagem) -->
                        <div 
                        v-if="false && msg.charts && msg.charts.length > 0" 
                        class="charts-container"
                        >
                            <div 
                                v-for="(chart, chartIdx) in msg.charts" 
                                :key="`chart-${chartIdx}`"
                                :ref="`chart-${index}-${chartIdx}`"
                                class="chart-wrapper"
                            >
                                <div class="chart-content"></div>
                            </div>
                        </div>
                    </div>

                    <!-- Loading (apenas quando não está em streaming) -->
                    <div v-if="carregando && !isStreaming" class="message message-assistant">
                        <div class="message-avatar">
                            <i class="fa fa-comments"></i>
                        </div>
                        <div class="message-content">
                            <div class="typing-indicator">
                                <span></span>
                                <span></span>
                                <span></span>
                            </div>
                        </div>
                    </div>
                    
                    <!-- Botão flutuante para voltar ao final -->
                    <transition name="fade">
                        <button 
                            v-if="showScrollToBottom"
                            class="scroll-to-bottom-btn"
                            @click="scrollToBottomAndEnable"
                            title="Rolar para o final"
                        >
                            <i class="fa fa-arrow-down"></i>
                        </button>
                    </transition>
                </div>

                <!-- Sugestões Collapse -->
                <div class="chat-suggestions-wrapper" v-if="mostrarSugestoes && sugestoes.length > 0 && !carregando && permitirEnvio">
                    <button 
                        class="suggestions-toggle"
                        @click="sugestoesVisiveis = !sugestoesVisiveis"
                        :class="{ 'active': sugestoesVisiveis }"
                        v-if="temInteracao"
                    >
                        <span>Sugestões</span>
                        <span class="badge">{{ sugestoes.length }}</span>
                        <i class="fa fa-chevron-down toggle-icon" :class="{ 'rotated': sugestoesVisiveis }"></i>
                    </button>
                    <div class="suggestions-header" v-else>
                        <span>Sugestões</span>
                    </div>
                    <b-collapse :visible="sugestoesVisiveis || !temInteracao">
                        <div class="suggestions-content mt-2">
                            <button
                                v-for="(sugestao, idx) in sugestoes"
                                :key="idx"
                                class="suggestion-chip"
                                @click="enviarSugestao(sugestao)"
                            >
                                {{ typeof sugestao === 'object' ? sugestao.text : sugestao }}
                            </button>
                        </div>
                    </b-collapse>
                </div>

                <!-- Input de Mensagem -->
                <div class="chat-input-container" v-if="permitirEnvio">
                    <div class="input-wrapper">
                        <textarea
                            v-model="mensagemInput"
                            placeholder="Digite sua mensagem..."
                            rows="1"
                            @keydown.enter.exact.prevent="enviarMensagem(false)"
                            @input="autoResize"
                            ref="inputArea"
                            :disabled="carregando || !sessaoAtiva"
                        ></textarea>
                        
                        <!-- Ícone de estrela para abrir biblioteca de modelos -->
                        <button 
                            type="button"
                            class="btn-input-icon"
                            @click="abrirBibliotecaPrompts"
                            title="Biblioteca de modelos"
                        >
                            <i class="fa fa-star-o"></i>
                        </button>
                        
                        <button 
                            v-if="!isStreaming"
                            class="btn btn-primary btn-send" 
                            @click="enviarMensagem(false)" 
                            :disabled="carregando || !mensagemInput.trim() || !sessaoAtiva"
                        >
                            <i class="fa fa-chevron-right"></i>
                        </button>
                        <button 
                            v-else
                            class="btn btn-primary btn-send" 
                            @click="cancelarStream"
                            title="Cancelar"
                        >
                            <i class="fa fa-stop"></i>
                        </button>
                    </div>
                </div>
            </template>
        </b-sidebar>
        
        <modal-tabela-expandida ref="modalTabela" />
        
        <modal-biblioteca-prompts 
            ref="modalBibliotecaPrompts" 
            @usar-prompt="usarPromptDaBiblioteca"
        />
        
        <modal-prompt-favoritos 
            ref="modalPromptFavoritos"
            @salvar="onSalvarFavorito"
        />

        <modal-contexto-sessao ref="modalContextoSessao" />
        
        <b-modal 
            id="modal-tool-response" 
            ref="modalToolResponse"
            title="Resposta da Ferramenta" 
            size="lg" 
            scrollable
        >
            <div class="tool-response-container">
                <div v-if="toolResponseData" class="tool-response-content">
                    <div class="tool-response-header mb-3">
                        <span class="badge badge-info mr-2">{{ toolResponseData.tool_name }}</span>
                        <span :class="toolResponseData.success ? 'badge badge-success' : 'badge badge-danger'">
                            {{ toolResponseData.success ? 'Sucesso' : 'Erro' }}
                        </span>
                    </div>
                    <pre class="tool-response-json">{{ formatToolResponse(toolResponseData.data) }}</pre>
                </div>
            </div>
            <template #modal-footer>
                <button class="btn btn-secondary" @click="$refs.modalToolResponse.hide()">
                    Fechar
                </button>
                <button 
                    v-if="false && toolResponseData && toolResponseData.chart_available" 
                    class="btn btn-info"
                    @click="gerarGraficoDoModal()"
                    :disabled="carregando"
                >
                    <i class="fa fa-bar-chart mr-1"></i>
                    Gerar Gráfico
                </button>
            </template>
        </b-modal>
        
        <!-- Debug Modal (MCP) -->
        <div v-if="debugModalVisible" class="debug-modal-overlay" @click.self="fecharDebugModal">
            <div class="debug-modal">
                <div class="debug-modal-header">
                    <h5><i class="fa fa-bug mr-2"></i>Debug Info</h5>
                    <button class="close-btn" @click="fecharDebugModal">
                        <i class="fa fa-times"></i>
                    </button>
                </div>
                <div class="debug-modal-body" v-if="debugModalData">
                    <!-- Metadata -->
                    <div class="debug-section">
                        <h6><i class="fa fa-info-circle mr-1"></i>Metadata</h6>
                        <div class="debug-grid">
                            <div v-if="debugModalData.metadata" class="debug-item">
                                <span class="label">Request ID:</span>
                                <code>{{ debugModalData.metadata.requestId || '-' }}</code>
                            </div>
                            <div v-if="debugModalData.metadata" class="debug-item">
                                <span class="label">Tempo:</span>
                                <code>{{ debugModalData.metadata.processingTimeMs || debugModalData.totalTime || 0 }}ms</code>
                            </div>
                            <div class="debug-item">
                                <span class="label">Session:</span>
                                <code>{{ debugModalData.sessionId || sessaoToken || '-' }}</code>
                            </div>
                            <div class="debug-item">
                                <span class="label">Tipo:</span>
                                <code>{{ debugModalData.responseType || '-' }}</code>
                            </div>
                        </div>
                    </div>

                    <!-- Tool Calls (detalhado) -->
                    <div v-if="debugModalData.toolCalls && debugModalData.toolCalls.length" class="debug-section">
                        <h6><i class="fa fa-wrench mr-1"></i>Ferramentas Executadas ({{ debugModalData.toolCalls.length }})</h6>
                        <div class="tool-calls-list">
                            <div v-for="(tc, idx) in debugModalData.toolCalls" :key="idx" class="tool-call-item" :class="{ 'tool-error': !tc.success }">
                                <div class="tool-call-header" @click="toggleToolExpand(idx)">
                                    <span class="tool-expand-icon">
                                        <i :class="expandedTools[idx] ? 'fa fa-chevron-down' : 'fa fa-chevron-right'"></i>
                                    </span>
                                    <span class="tool-num">{{ idx + 1 }}</span>
                                    <span class="tool-name">{{ tc.displayName || tc.toolId }}</span>
                                    <span class="tool-badges">
                                        <span :class="tc.success ? 'badge badge-success' : 'badge badge-danger'">
                                            {{ tc.success ? 'OK' : 'Erro' }}
                                        </span>
                                        <span class="badge badge-secondary">{{ tc.durationMs }}ms</span>
                                        <span v-if="tc.resultCount !== undefined" class="badge badge-info">
                                            {{ tc.resultCount }} reg.
                                        </span>
                                    </span>
                                </div>
                                
                                <!-- Conteúdo expandido -->
                                <div v-if="expandedTools[idx]" class="tool-call-expanded">
                                    <!-- Parâmetros enviados -->
                                    <div class="tool-section">
                                        <div class="tool-section-title">
                                            <i class="fa fa-paper-plane mr-1"></i> Parâmetros Enviados
                                        </div>
                                        <div v-if="!tc.params || Object.keys(tc.params).length === 0" class="text-muted small">
                                            <em>(sem parâmetros)</em>
                                        </div>
                                        <div v-else class="params-grid">
                                            <div v-for="(value, key) in tc.params" :key="key" class="param-item">
                                                <span class="param-key">{{ key }}:</span>
                                                <code class="param-value">{{ formatParamValue(value) }}</code>
                                            </div>
                                        </div>
                                    </div>
                                    
                                    <!-- Erro se houver -->
                                    <div v-if="tc.error" class="tool-error-message">
                                        <i class="fa fa-exclamation-triangle mr-1"></i>
                                        <code>{{ tc.error }}</code>
                                    </div>
                                    
                                    <!-- Resposta da API -->
                                    <div class="tool-section">
                                        <div class="tool-section-title" @click.stop="toggleToolResponse(idx)">
                                            <i :class="expandedToolResponses[idx] ? 'fa fa-minus-square' : 'fa fa-plus-square'" class="mr-1"></i>
                                            <i class="fa fa-download mr-1"></i> Resposta da API
                                            <span v-if="tc.resultCount !== undefined" class="text-muted small ml-2">({{ tc.resultCount }} registros)</span>
                                        </div>
                                        <div v-if="expandedToolResponses[idx]" class="tool-response-debug">
                                            <pre class="raw-json" v-html="syntaxHighlight(tc.result || tc.data || '(sem dados)')"></pre>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Fallback: Tools simples (se não tiver toolCalls) -->
                    <div v-else-if="debugModalData.toolsExecuted && debugModalData.toolsExecuted.length" class="debug-section">
                        <h6><i class="fa fa-wrench mr-1"></i>Ferramentas Executadas</h6>
                        <div class="tools-badges">
                            <span v-for="tool in debugModalData.toolsExecuted" :key="tool" class="badge badge-info mr-1 mb-1">
                                {{ tool }}
                            </span>
                        </div>
                        <!-- Parâmetros da última tool -->
                        <div v-if="debugModalData.toolParams && Object.keys(debugModalData.toolParams).length > 0" class="mt-2">
                            <small class="text-muted">Parâmetros (última tool):</small>
                            <div class="params-grid">
                                <div v-for="(value, key) in debugModalData.toolParams" :key="key" class="param-item">
                                    <span class="param-key">{{ key }}:</span>
                                    <code class="param-value">{{ value }}</code>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Data Stats -->
                    <div v-if="debugModalData.rawData" class="debug-section">
                        <h6><i class="fa fa-bar-chart mr-1"></i>Dados Retornados</h6>
                        <div class="debug-grid">
                            <div class="debug-item">
                                <span class="label">Registros:</span>
                                <code>{{ getDataCount(debugModalData.rawData) }}</code>
                            </div>
                            <div v-if="debugModalData.rawData._truncated" class="debug-item">
                                <span class="label">Tamanho:</span>
                                <code>{{ debugModalData.rawData._size }} bytes (truncado)</code>
                            </div>
                        </div>
                    </div>

                    <!-- Thoughts -->
                    <div v-if="debugModalData.thoughts && debugModalData.thoughts.length" class="debug-section">
                        <h6><i class="fa fa-lightbulb-o mr-1"></i>Passos ReAct</h6>
                        <div class="thoughts-list">
                            <div v-for="(t, idx) in debugModalData.thoughts" :key="idx" class="thought-item">
                                <div class="thought-num">{{ idx + 1 }}</div>
                                <div class="thought-body">
                                    <strong>{{ t.step }}</strong>
                                    <p>{{ t.content }}</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Tool Response -->
                    <div v-if="debugModalData.rawData" class="debug-section">
                        <h6 @click="toolResponseExpanded = !toolResponseExpanded" style="cursor: pointer;">
                            <i :class="toolResponseExpanded ? 'fa fa-minus-square' : 'fa fa-plus-square'" class="mr-1"></i>
                            <i class="fa fa-code mr-1"></i>Resposta da Tool (clique para expandir)
                        </h6>
                        <div v-if="toolResponseExpanded" class="tool-response-container-debug">
                            <pre class="raw-json" v-html="syntaxHighlight(debugModalData.rawData)"></pre>
                        </div>
                    </div>

                    <!-- Stream Log -->
                    <div v-if="debugModalData.streamLog && debugModalData.streamLog.length" class="debug-section">
                        <h6><i class="fa fa-list mr-1"></i>Stream Log ({{ debugModalData.streamLog.length }})</h6>
                        <div class="stream-log">
                            <div v-for="(log, idx) in filteredStreamLog" :key="idx" class="log-row">
                                <div class="log-header" @click="toggleLog(idx)">
                                    <i :class="expandedLogs[idx] ? 'fa fa-minus-square' : 'fa fa-plus-square'"></i>
                                    <span class="log-type" :class="'type-' + log.type">{{ log.type }}</span>
                                    <span class="log-preview" v-if="!expandedLogs[idx]">{{ truncate(JSON.stringify(log.data), 50) }}</span>
                                </div>
                                <pre v-if="expandedLogs[idx]" class="log-json" v-html="syntaxHighlight(log.data)"></pre>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import Highcharts from 'highcharts'
import autenticacao from '@/shared/services/autenticacao'
import { create } from '@/shared/services/api.js'
import ModalTabelaExpandida from './ModalTabelaExpandida.vue'
import ModalBibliotecaPrompts from './ModalBibliotecaPrompts.vue'
import ModalPromptFavoritos from './ModalPromptFavoritos.vue'
import ModalContextoSessao from './ModalContextoSessao.vue'

const STORAGE_KEY_PREFIX = 'chat_ia_session_'

export default {
    name: 'ChatIA',
    
    components: {
        ModalTabelaExpandida,
        ModalBibliotecaPrompts,
        ModalPromptFavoritos,
        ModalContextoSessao
    },
    props: {
        // ID único do sidebar (permite múltiplas instâncias)
        sidebarId: {
            type: String,
            default: 'sidebar-chat-ia'
        },
        // Mostrar sugestões de perguntas
        mostrarSugestoes: {
            type: Boolean,
            default: true
        },
        // Permitir envio de mensagens (false = readonly)
        permitirEnvio: {
            type: Boolean,
            default: true
        },
        // Mensagem inicial a ser enviada automaticamente
        mensagemInicial: {
            type: String,
            default: ''
        },
        // Exibir a mensagem inicial no chat (se false, só mostra a resposta)
        exibirMensagemInicial: {
            type: Boolean,
            default: true
        },
        // Habilitar streaming de respostas (digitando em tempo real)
        habilitarStreaming: {
            type: Boolean,
            default: true
        },
        // Persistir sessão no localStorage
        persistirSessao: {
            type: Boolean,
            default: true
        },
        // Mostrar botão de debug nas ferramentas executadas
        mostrarDebug: {
            type: Boolean,
            default: true
        }
    },
    data() {
        return {
            sidebarVisivel: false,
            mensagens: [],
            mensagemInput: '',
            carregando: false,
            sessaoToken: null,
            sessaoId: null,
            sessaoAtiva: false,
            // Isolamento: empresa/loja em que a sessao foi criada. Se mudar
            // (mesmo user troca de empresa), descartamos a sessao local.
            sessaoEmpresaId: null,
            sessaoLojaId: null,
            sugestoes: [],
            sugestoesVisiveis: false,
            sugestoesPadrao: [
                'Contas a pagar vencendo esta semana',
                'DRE do mês atual',
                'Contas a receber em atraso',
                'Últimas vendas realizadas',
                'Resumo financeiro do dia'
            ],
            ultimaConsulta: '',
            alturaScroll: 0,
            mensagemInicialEnviada: false,
            carregandoHistorico: false,
            historicoCarregado: false,
            paginaHistorico: 1,
            limitePorPagina: 20,
            temMaisHistorico: true,
            isMobile: window.innerWidth <= 768,
            chatExpandido: localStorage.getItem('chat-ia-expandido') === 'true',
            // Streaming
            isStreaming: false,
            streamingMessage: null,
            currentThinking: '',
            currentSteps: [],
            abortController: null,
            useStreaming: true, // Será atualizado pelo watch da prop habilitarStreaming
            // Resposta da ferramenta
            toolResponseData: null,
            currentToolMessage: null,
            // Quota de tokens
            quota: null,
            // Token de autenticação usado na sessão atual
            sessaoAuthToken: null,
            // Debug modal
            debugModalVisible: false,
            debugModalData: null,
            currentDebugInfo: null,
            expandedLogs: {},
            toolResponseExpanded: false,
            expandedTools: {},
            expandedToolResponses: {},
            // Auto-scroll control
            autoScrollEnabled: true,
            showScrollToBottom: false,
        }
    },
    computed: {
        storageKey() {
            return STORAGE_KEY_PREFIX + this.sidebarId
        },
        temInteracao() {
            // Verifica se há mensagens do usuário (interação real)
            return this.mensagens.some(m => m.role === 'user')
        },
        sidebarWidth() {
            if (this.isMobile) {
                return '100%'
            }
            return this.chatExpandido ? '50%' : '450px'
        },
        /** Stream Log filtrado: remove chunks de content/message (poluem o debug) */
        filteredStreamLog() {
            if (!this.debugModalData?.streamLog) return []
            return this.debugModalData.streamLog.filter(log => log.type !== 'content' && log.type !== 'message')
        }
    },
    created() {
        const apiUrl = process.env.VUE_APP_API_IA_URL || 'https://ia.click.app/api'
        // intranet (FastAPI) espera JSON, não multipart
        this.apiIA = create(apiUrl, 'application/json')
    },
    mounted() {
        window.addEventListener('resize', this.handleResize)
        this.useStreaming = this.habilitarStreaming
        // Registra função global para expandir tabelas
        window.expandTable = this.expandTable
        // NOTA: nao chamar atualizarQuota aqui — ChatIA monta em toda pagina interna
        // (Interno.vue), entao isso bateria no intranet em toda navegacao. A quota
        // e carregada quando o modal abre (abrirSidebar) ou apos cada mensagem.
    },
    watch: {
        habilitarStreaming(newVal) {
            this.useStreaming = newVal
        }
    },
    beforeDestroy() {
        window.removeEventListener('resize', this.handleResize)
        // Remove função global
        if (window.expandTable === this.expandTable) {
            delete window.expandTable
        }
    },
    methods: {
        getWelcomeMessage() {
            return `Olá! Sou seu assistente com IA.\n
                    Posso consultar informações diretamente no ERP, como clientes, pedidos, financeiro e outros registros, de acordo com suas permissões de acesso.
                    As consultas são feitas de forma segura para ajudar você a encontrar respostas mais rápido.
                    Como posso ajudar?`
        },
        /**
         * Agrupa os steps por categoria, mostrando apenas o último item de cada grupo
         * Ex: Múltiplos "Analisando..." viram um único grupo com o último detalhe
         */
        getGroupedSteps(steps) {
            const groups = {}
            const order = []
            
            for (const step of steps) {
                // Extrair categoria (parte antes de "..." ou a ação principal)
                const category = this.extractStepCategory(step.action || step.step)
                const detail = this.extractStepDetail(step.action || step.step)
                
                if (!groups[category]) {
                    groups[category] = {
                        category: category,
                        status: step.status,
                        lastItem: detail,
                        count: 1
                    }
                    order.push(category)
                } else {
                    groups[category].lastItem = detail || groups[category].lastItem
                    groups[category].count++
                    // Atualizar status se o novo for mais relevante
                    if (step.status === 'running' || 
                        (step.status === 'error' && groups[category].status !== 'running')) {
                        groups[category].status = step.status
                    }
                }
            }
            
            // Retornar na ordem em que apareceram
            const result = {}
            for (const cat of order) {
                result[cat] = groups[cat]
            }
            return result
        },
        /**
         * Extrai a categoria principal do step (ex: "Analisando sua solicitação" -> "Analisando sua solicitação")
         */
        extractStepCategory(action) {
            if (!action) return 'Processando'
            
            // Steps que são resultados/detalhes - devem ser agrupados na categoria "Analisando"
            const resultPatterns = [
                'Identificad',      // Identificadas X opções
                'Encontrad',        // Encontrados X resultados
                'Localizado',
                'opções relevantes',
                'resultado'
            ]
            
            // Se é um resultado, agrupar em "Analisando sua solicitação"
            for (const pattern of resultPatterns) {
                if (action.toLowerCase().includes(pattern.toLowerCase())) {
                    return 'Analisando sua solicitação'
                }
            }
            
            // Categorias conhecidas que devem ser agrupadas
            const knownCategories = [
                'Analisando sua solicitação',
                'Analisando',
                'Aplicando filtros',
                'Processando dados',
                'Consultando',
                'Buscando',
                'Carregando',
                'Pensando na melhor abordagem'
            ]
            
            // Verificar se começa com uma categoria conhecida
            for (const cat of knownCategories) {
                if (action.toLowerCase().startsWith(cat.toLowerCase())) {
                    return cat.replace(/^Analisando$/, 'Analisando sua solicitação')
                }
            }
            
            // Se contém "•" ou é um item de lista, extrair a categoria base
            if (action.includes('•')) {
                return action.split('•')[0].trim() || 'Processando'
            }
            
            // Retornar a ação como categoria
            return action
        },
        /**
         * Extrai o detalhe/item específico do step (ex: nome da listagem)
         */
        extractStepDetail(action) {
            if (!action) return null
            
            // Se contém "•", o detalhe é o que vem depois
            if (action.includes('•')) {
                const parts = action.split('•')
                return parts.length > 1 ? parts[parts.length - 1].trim() : null
            }
            
            // Se é um resultado (ex: "Identificadas 7 opções relevantes")
            const resultPatterns = [
                'Identificad',
                'Encontrad',
                'Localizado',
                'opções relevantes'
            ]
            for (const pattern of resultPatterns) {
                if (action.toLowerCase().includes(pattern.toLowerCase())) {
                    return action
                }
            }
            
            // Se é uma listagem ou item específico (não uma ação genérica)
            if (action.includes('Listagem') || action.includes('Relatório') || 
                action.includes('Cadastro') || action.includes('Pedido')) {
                return action
            }
            
            // Ignorar steps genéricos como "Analisando sua solicitação..."
            if (action.toLowerCase().includes('analisando') && !action.includes('Identificad')) {
                return null
            }
            
            return null
        },
        handleResize() {
            this.isMobile = window.innerWidth <= 768
        },
        abrir() {
            this.sidebarVisivel = true
        },
        preencherInput(texto) {
            this.mensagemInput = texto || ''
        },
        async abrirSidebar() {
            const body = document.scrollingElement || document.body
            this.alturaScroll = body.scrollTop
            const html = document.documentElement
            html.style.overflow = 'hidden'
            
            // Verifica se o usuário logado mudou (trocar de conta) OU se trocou
            // de empresa/loja com o mesmo user. Sem esse 2o check, mesmo user
            // logado em empresa B veria conversa salva na empresa A.
            const currentAuthToken = autenticacao.pegaToken() || ''
            const currentEmpresaId = (this.$usuarioLogado && this.$usuarioLogado.Empresa && this.$usuarioLogado.Empresa.id) || null
            const currentLojaId = (this.$usuarioLogado && this.$usuarioLogado.Loja && this.$usuarioLogado.Loja.id) || null
            if (
                this.sessaoToken && (
                    (this.sessaoAuthToken && this.sessaoAuthToken !== currentAuthToken) ||
                    (this.sessaoEmpresaId != null && this.sessaoEmpresaId !== currentEmpresaId) ||
                    (this.sessaoLojaId != null && this.sessaoLojaId !== currentLojaId)
                )
            ) {
                // Usuário ou empresa/loja diferente — força nova sessão
                this.limparSessaoLocal()
            }
            
            console.log('[ChatIA] abrirSidebar v2 (fast-path + iniciarSessao)')
            // Fast-path: se ja tem em memoria ou recupera da storage, evita chamar o servidor
            const sessaoRecuperada = this.sessaoToken ? true : this.recuperarSessao()

            // Sem sessao local: chama /agent_start no servidor.
            // Pra user_id > 0, o backend retorna a MESMA session_id (get_or_create, TTL 30d),
            // entao o carregarHistorico interno traz o historico real de conversas anteriores.
            if (!this.sessaoToken) {
                await this.iniciarSessao()
                // Se por algum motivo /agent_start falhou, cai num token local pra nao travar o modal
                if (!this.sessaoToken) {
                    this.sessaoToken = 'chat-ia-mcp-' + Date.now()
                    this.sessaoId = this.sessaoToken
                    this.sessaoAtiva = true
                    this.sessaoAuthToken = currentAuthToken
                    this.sessaoEmpresaId = currentEmpresaId
                    this.sessaoLojaId = currentLojaId
                    this.sugestoes = this.sugestoesPadrao.map(s => ({ text: s, command: s }))
                }
                if (this.mensagens.length === 0) {
                    this.mensagens = [{
                        role: 'assistant',
                        content: this.getWelcomeMessage()
                    }]
                }
            } else if (sessaoRecuperada && !this.historicoCarregado) {
                // Sessao recuperada - aguarda carregarHistorico definir as mensagens
            }
            
            // Carrega quota apos modal abrir (substitui chamada antiga do mounted())
            this.atualizarQuota()

            // Scroll para o final após abrir
            this.$nextTick(() => {
                setTimeout(() => {
                    this.scrollToBottom()
                }, 100)
            })
            
            // Envia mensagem inicial se configurada, ainda não enviada e sessão é nova (sem histórico)
            if (this.mensagemInicial && !this.mensagemInicialEnviada && this.sessaoAtiva && this.mensagens.length <= 1) {
                this.mensagemInicialEnviada = true
                this.mensagemInput = this.mensagemInicial
                // Se exibirMensagemInicial é false, oculta a mensagem do usuário (só mostra resposta)
                await this.enviarMensagem(!this.exibirMensagemInicial)
            }
        },
        fecharSidebar() {
            const body = document.scrollingElement || document.body
            body.scrollTop = this.alturaScroll
            const html = document.documentElement
            html.style.overflow = ''
            
            // Salva sessão no storage
            if (this.persistirSessao) {
                this.salvarSessao()
            }
        },
        salvarSessao() {
            if (!this.sessaoToken) return
            
            const sessaoData = {
                token: this.sessaoToken,
                id: this.sessaoId,
                mensagens: this.mensagens,
                sugestoes: this.sugestoes,
                quota: this.quota,
                timestamp: Date.now(),
                authToken: autenticacao.pegaToken() || '',
                // Isolamento por empresa/loja: evita user que trocou de
                // empresa ver conversa cacheada da empresa anterior.
                empresaId: (this.$usuarioLogado && this.$usuarioLogado.Empresa && this.$usuarioLogado.Empresa.id) || null,
                lojaId: (this.$usuarioLogado && this.$usuarioLogado.Loja && this.$usuarioLogado.Loja.id) || null
            }
            
            try {
                sessionStorage.setItem(this.storageKey, JSON.stringify(sessaoData))
            } catch (e) {
                console.warn('Erro ao salvar sessão:', e)
            }
        },
        recuperarSessao() {
            try {
                const stored = sessionStorage.getItem(this.storageKey)
                if (!stored) return false
                
                const sessaoData = JSON.parse(stored)
                
                // Verifica se a sessão não expirou (30 min)
                const maxAge = 30 * 60 * 1000
                if (Date.now() - sessaoData.timestamp > maxAge) {
                    sessionStorage.removeItem(this.storageKey)
                    return false
                }
                
                const currentAuthToken = autenticacao.pegaToken() || ''
                const currentEmpresaId = (this.$usuarioLogado && this.$usuarioLogado.Empresa && this.$usuarioLogado.Empresa.id) || null
                const currentLojaId = (this.$usuarioLogado && this.$usuarioLogado.Loja && this.$usuarioLogado.Loja.id) || null
                if (
                    (sessaoData.authToken && sessaoData.authToken !== currentAuthToken) ||
                    (sessaoData.empresaId != null && sessaoData.empresaId !== currentEmpresaId) ||
                    (sessaoData.lojaId != null && sessaoData.lojaId !== currentLojaId)
                ) {
                    // Mudou user, empresa ou loja — descarta sessao salva
                    sessionStorage.removeItem(this.storageKey)
                    return false
                }

                this.sessaoToken = sessaoData.token
                this.sessaoId = sessaoData.id || null
                this.sessaoEmpresaId = sessaoData.empresaId || null
                this.sessaoLojaId = sessaoData.lojaId || null
                this.sessaoAtiva = true
                this.sessaoAuthToken = sessaoData.authToken || currentAuthToken
                
                if (sessaoData.quota) {
                    this.quota = sessaoData.quota
                }
                
                if (sessaoData.sugestoes && sessaoData.sugestoes.length > 0) {
                    this.sugestoes = sessaoData.sugestoes
                } else {
                    this.sugestoes = this.sugestoesPadrao.map(s => ({ text: s, command: s }))
                }
                
                // Carrega histórico do backend (não usa mensagens do storage)
                this.carregarHistorico()
                this.atualizarQuota()
                
                return true
            } catch (e) {
                console.warn('Erro ao recuperar sessão:', e)
                return false
            }
        },
        async carregarHistorico(anterior = false) {
            if (!this.sessaoToken || this.carregandoHistorico) return
            if (anterior && !this.temMaisHistorico) return
            
            this.carregandoHistorico = true
            
            try {
                const offset = anterior ? this.mensagens.length : 0
                const response = await this.$api.cPost('/ia/agent_history', {
                    session_token: this.sessaoToken,
                    limit: this.limitePorPagina,
                    offset: offset
                })
                
                const dados = response.dados || response.data || {}
                
                if (response && response.status === 'success' && dados.messages) {
                    // Filtra apenas mensagens de user e assistant com conteúdo
                    // Ignora mensagens duplicadas de gráfico (que começam com 📊 e são só indicadores)
                    const mensagensFormatadas = dados.messages
                        .filter(m => {
                            if (!m.content || !m.content.trim()) return false
                            if (m.role !== 'user' && m.role !== 'assistant') return false
                            // Ignora mensagens que são apenas indicadores de gráfico gerado
                            const meta = m.metadata || {}
                            if (meta.chart_generated === true && m.content.startsWith('📊 ') && m.content.length < 200) {
                                return false
                            }
                            return true
                        })
                        .map((m) => {
                            const meta = m.metadata || {}
                            const toolCalls = m.tool_calls || []
                            return {
                                role: m.role,
                                content: m.content,
                                tools_executed: meta.tools_executed || toolCalls.length || (m.role === 'assistant' ? 1 : 0),
                                chart_available: meta.chart_available || false,
                                chart_tool_name: meta.chart_tool_name || null,
                                chart_tool_data: meta.chart_tool_data || null,
                                chart_generated: meta.chart_generated || false,
                                charts: meta.charts || [],
                                fromHistory: true
                            }
                        })
                    
                    if (anterior) {
                        // Guarda posição do scroll antes de adicionar
                        const container = this.$refs.chatMessages
                        const scrollHeightAntes = (container && container.scrollHeight) || 0
                        
                        // Deduplicação: remove mensagens que já existem no array atual
                        const existingContents = new Set(
                            this.mensagens.map(m => (m.role + ':' + (m.content || '').substring(0, 100)))
                        )
                        const novasMensagens = mensagensFormatadas.filter(m => {
                            const key = m.role + ':' + (m.content || '').substring(0, 100)
                            return !existingContents.has(key)
                        })
                        
                        if (novasMensagens.length === 0) {
                            // Nenhuma mensagem nova — provavelmente não tem mais histórico
                            this.temMaisHistorico = false
                            return
                        }
                        
                        // Adiciona no início (mensagens mais antigas vem primeiro)
                        this.mensagens = [...novasMensagens, ...this.mensagens]
                        
                        // Mantém posição do scroll
                        this.$nextTick(() => {
                            if (container) {
                                const scrollHeightDepois = container.scrollHeight
                                container.scrollTop = scrollHeightDepois - scrollHeightAntes
                            }
                        })
                    } else {
                        // Primeira carga - substitui tudo
                        if (mensagensFormatadas.length > 0) {
                            this.mensagens = mensagensFormatadas
                        } else if (this.mensagens.length === 0) {
                            // Se não tem histórico E não tem mensagens locais, mostra mensagem inicial
                            this.mensagens = [{
                                role: 'assistant',
                                content: this.getWelcomeMessage()
                            }]
                        }
                        // Se já tem mensagem de boas-vindas local, mantém
                        this.scrollToBottom()
                    }
                    
                    // Verifica se tem mais histórico
                    this.temMaisHistorico = mensagensFormatadas.length >= this.limitePorPagina
                    this.historicoCarregado = true
                    
                    // Atualiza info da sessão se veio
                    if (dados.session) {
                        this.sessaoId = dados.session.id
                    }
                    
                    // Gráficos do histórico serão carregados sob demanda (botão "Gráfico disponível")
                }
            } catch (error) {
                console.error('Erro ao carregar histórico:', error)
                // Se falhar e não tem nenhuma mensagem, mostra mensagem inicial
                if (!anterior && this.mensagens.length === 0) {
                    this.mensagens = [{
                        role: 'assistant',
                        content: this.getWelcomeMessage()
                    }]
                }
            } finally {
                this.carregandoHistorico = false
            }
        },
        onScrollMessages(event) {
            const container = event.target
            // Se chegou no topo e tem mais histórico (com debounce para evitar chamadas duplas)
            if (container.scrollTop < 50 && this.temMaisHistorico && !this.carregandoHistorico) {
                // Debounce: espera 300ms antes de carregar mais
                if (this._scrollDebounce) clearTimeout(this._scrollDebounce)
                this._scrollDebounce = setTimeout(() => {
                    if (this.temMaisHistorico && !this.carregandoHistorico) {
                        this.carregarHistorico(true)
                    }
                }, 300)
            }
            
            // Controle de auto-scroll
            const isAtBottom = container.scrollHeight - container.scrollTop - container.clientHeight < 50
            
            if (isAtBottom) {
                // Chegou no final - reativa auto-scroll
                this.autoScrollEnabled = true
                this.showScrollToBottom = false
            } else if (this.isStreaming) {
                // Rolou para cima durante streaming - desativa auto-scroll e mostra botão
                this.autoScrollEnabled = false
                this.showScrollToBottom = true
            }
        },
        limparSessaoLocal() {
            this.sessaoToken = null
            this.sessaoId = null
            this.sessaoAtiva = false
            this.sessaoAuthToken = null
            this.sessaoEmpresaId = null
            this.sessaoLojaId = null
            this.mensagens = []
            this.mensagemInicialEnviada = false
            this.historicoCarregado = false
            this.temMaisHistorico = true
            this.quota = null
            
            try {
                sessionStorage.removeItem(this.storageKey)
            } catch (e) {
                console.warn('Erro ao limpar sessão:', e)
            }
        },
        async iniciarSessao(forcarNova = false) {
            // Se forçar nova sessão, limpa a atual
            if (forcarNova) {
                this.limparSessaoLocal()
            }
            
            if (this.sessaoToken) return

            this.carregando = true
            try {
                const response = await this.$api.cPost('/ia/agent_start', {
                    language: 'pt_BR',
                    force_new: forcarNova
                })

                const dados = response.dados || response.data
                if (response && response.status === 'success' && dados && dados.session) {
                    this.sessaoToken = dados.session.token
                    this.sessaoId = dados.session.id
                    this.sessaoAtiva = true
                    this.sessaoAuthToken = autenticacao.pegaToken() || ''
                    this.sessaoEmpresaId = (this.$usuarioLogado && this.$usuarioLogado.Empresa && this.$usuarioLogado.Empresa.id) || null
                    this.sessaoLojaId = (this.$usuarioLogado && this.$usuarioLogado.Loja && this.$usuarioLogado.Loja.id) || null
                    
                    if (dados && dados.quota) {
                        this.quota = dados.quota
                    }
                    
                    if (dados && dados.suggestions && dados.suggestions.length > 0) {
                        this.sugestoes = dados.suggestions.map(s => ({
                            text: s.text || s.suggestion_text || s,
                            command: s.command || s.suggestion_command || s.text || s.suggestion_text || s
                        }))
                    } else {
                        this.sugestoes = this.sugestoesPadrao.map(s => ({ text: s, command: s }))
                    }
                    
                    // Salva sessão inicial e carrega histórico apenas se persistir
                    if (this.persistirSessao) {
                        this.salvarSessao()
                        // Carrega histórico da sessão (pode ter mensagens anteriores)
                        await this.carregarHistorico()
                    }
                } else {
                    this.$dialog.error((response && response.message) || 'Erro ao iniciar sessão do agente.')
                    this.sessaoAtiva = false
                }
            } catch (error) {
                console.error('Erro ao iniciar sessão:', error)
                this.$dialog.error('Erro ao conectar com o assistente.')
                this.sessaoAtiva = false
            } finally {
                this.carregando = false
            }
        },
        async enviarMensagem(ocultarMensagemUsuario = false) {
            if (!this.mensagemInput.trim() || this.carregando) return
            
            // Para MCP, sessão é opcional - usa ID gerado se não tiver
            if (!this.sessaoToken) {
                this.sessaoToken = 'chat-ia-mcp-' + Date.now()
                this.sessaoAtiva = true
                this.sessaoAuthToken = autenticacao.pegaToken() || ''
                this.sessaoEmpresaId = (this.$usuarioLogado && this.$usuarioLogado.Empresa && this.$usuarioLogado.Empresa.id) || null
                this.sessaoLojaId = (this.$usuarioLogado && this.$usuarioLogado.Loja && this.$usuarioLogado.Loja.id) || null
            }

            const mensagemUsuario = this.mensagemInput.trim()
            this.ultimaConsulta = mensagemUsuario
            this.mensagemInput = ''
            this.resetTextarea()

            if (!ocultarMensagemUsuario) {
                this.mensagens.push({
                    role: 'user',
                    content: mensagemUsuario
                })
            }

            this.scrollToBottom()
            this.carregando = true

            if (this.useStreaming) {
                await this.enviarMensagemStream(mensagemUsuario)
            } else {
                await this.enviarMensagemNormal(mensagemUsuario)
            }
        },
        async enviarMensagemNormal(mensagemUsuario) {
            try {
                const response = await this.$api.cPost('/ia/agent_chat', {
                    session_token: this.sessaoToken,
                    message: mensagemUsuario,
                    language: 'pt_BR'
                })

                if (response && response.status === 'success') {
                    const dados = response.dados || response.data || {}
                    const novaMensagem = {
                        role: 'assistant',
                        content: dados.message || response.message || 'Sem resposta',
                        tools_executed: dados.tools_executed || 0,
                        chart_available: dados.chart_available || false,
                        chart_tool_name: dados.chart_tool_name || null,
                        chart_tool_data: dados.chart_tool_data || null,
                        chart_generated: false,
                        charts: []
                    }
                    this.mensagens.push(novaMensagem)
                    
                    if (this.persistirSessao) {
                        this.salvarSessao()
                    }
                } else {
                    const mensagemErro = this.filtrarErroTecnico(response && response.message)
                    this.mensagens.push({
                        role: 'assistant',
                        content: mensagemErro
                    })
                }
            } catch (error) {
                console.error('Erro ao enviar mensagem:', error)
                this.mensagens.push({
                    role: 'assistant',
                    content: 'Desculpe, ocorreu um erro. Tente novamente.'
                })
            } finally {
                this.carregando = false
                this.scrollToBottom()
                // Atualiza widget de consumo apos cada resposta (modo nao-stream)
                setTimeout(() => this.atualizarQuota(), 300)
            }
        },
        async enviarMensagemStream(mensagemUsuario) {
            this.isStreaming = true
            this.currentThinking = ''
            this.currentSteps = []
            this.streamBuffer = ''
            // Reativa auto-scroll ao enviar nova mensagem
            this.autoScrollEnabled = true
            this.showScrollToBottom = false
            
            // Inicializa debug info
            this.currentDebugInfo = {
                streamLog: [],
                thoughts: [],
                toolsExecuted: [],
                toolParams: null,
                metadata: null,
                sessionId: this.sessaoToken,
                responseType: null,
                rawData: null,
                startTime: Date.now()
            }
            
            const assistantMessage = {
                role: 'assistant',
                content: '',
                isStreaming: true,
                thinking: '',
                steps: [],
                tools_executed: 0,
                chart_available: false,
                chart_tool_name: null,
                chart_tool_data: null,
                chart_generated: false,
                charts: [],
                // MCP v2 debug info
                isConfirmation: false,
                isSelection: false,
                debugInfo: null
            }
            this.mensagens.push(assistantMessage)
            this.streamingMessage = assistantMessage

            try {
                this.abortController = new AbortController()

                const baseUrl = process.env.VUE_APP_API_URL
                console.log('[ChatIA] Enviando para MCP:', baseUrl)
                const erpToken = autenticacao.pegaToken() || ''
                
                // Monta histórico de conversação para o MCP
                const conversationHistory = this.getConversationHistory()
                
                const response = await fetch(`${baseUrl}/ia/mcp_v2_chat_stream`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'X-Requested-With': 'XMLHttpRequest',
                        'x-token-auth': erpToken,
                        'host-origin': window.location.host
                    },
                    body: JSON.stringify({
                        session_token: this.sessaoToken || null,
                        message: mensagemUsuario,
                        language: 'pt_BR',
                        conversation_history: conversationHistory
                    }),
                    signal: this.abortController.signal,
                    credentials: 'include'
                })

                if (!response.ok) {
                    throw new Error('Erro na conexão: ' + response.status)
                }

                if (!response.body) {
                    throw new Error('Response body não disponível')
                }

                const reader = response.body.getReader()
                const decoder = new TextDecoder()
                let streamDone = false

                while (!streamDone) {
                    const { done, value } = await reader.read()
                    
                    if (done) {
                        streamDone = true
                        continue
                    }

                    const chunk = decoder.decode(value, { stream: true })
                    this.processStreamChunk(chunk)
                }

            } catch (error) {
                if (error.name === 'AbortError') {
                    assistantMessage.content += '\n\n_(Cancelado pelo usuário)_'
                } else {
                    console.error('Erro no streaming:', error)
                    assistantMessage.content = 'Desculpe, ocorreu um erro. Tente novamente.'
                }
            } finally {
                this.isStreaming = false
                this.carregando = false
                this.abortController = null
                this.streamBuffer = ''
                
                const ultimaMensagem = this.mensagens[this.mensagens.length - 1]
                if (ultimaMensagem && ultimaMensagem.role === 'assistant' && ultimaMensagem.isStreaming) {
                    this.$set(ultimaMensagem, 'isStreaming', false)
                }
                
                this.streamingMessage = null
                this.scrollToBottom()

                if (this.persistirSessao) {
                    this.salvarSessao()
                }
            }
        },
        processStreamChunk(chunk) {
            this.streamBuffer = (this.streamBuffer || '') + chunk
            
            const lines = this.streamBuffer.split('\n')
            
            if (!this.streamBuffer.endsWith('\n')) {
                this.streamBuffer = lines.pop() || ''
            } else {
                this.streamBuffer = ''
            }
            
            for (const line of lines) {
                const trimmedLine = line.trim()
                if (!trimmedLine.startsWith('data: ')) continue
                
                const data = trimmedLine.slice(6).trim()
                if (!data || data === '[DONE]') continue

                try {
                    const event = JSON.parse(data)
                    this.handleStreamEvent(event)
                } catch (e) {
                    this.streamBuffer = 'data: ' + data + '\n' + this.streamBuffer
                    break
                }
            }
        },
        handleStreamEvent(event) {
            if (!this.streamingMessage) return
            
            // Registra evento no debug log
            if (this.currentDebugInfo) {
                this.currentDebugInfo.streamLog.push({
                    type: event.type,
                    data: event.data || event.step || event.content,
                    timestamp: event.timestamp || new Date().toISOString()
                })
            }

            switch (event.type) {
                // === Eventos do MCP v2 ===
                case 'step':
                    if (event.step) {
                        const stepName = event.step.name || ''
                        const stepDetails = event.step.details || ''
                        
                        // Adiciona ao debug thoughts
                        if (this.currentDebugInfo) {
                            this.currentDebugInfo.thoughts.push({
                                step: stepName,
                                content: stepDetails,
                                timestamp: new Date().toISOString()
                            })
                        }
                        
                        // Adiciona ao array de steps visuais
                        this.currentSteps.push({
                            step: stepName,
                            action: stepDetails,
                            status: event.step.status === 'running' ? 'running' : 'done'
                        })
                        this.streamingMessage.steps = [...this.currentSteps]
                        this.currentThinking = stepDetails || stepName
                        this.streamingMessage.thinking = this.currentThinking
                    }
                    break

                case 'thought':
                    if (event.data) {
                        const stepName = event.data.step || ''
                        const stepContent = event.data.content || ''
                        
                        // Adiciona ao debug thoughts
                        if (this.currentDebugInfo) {
                            this.currentDebugInfo.thoughts.push({
                                step: stepName,
                                content: stepContent,
                                timestamp: new Date().toISOString()
                            })
                        }
                        
                        this.currentSteps.push({
                            step: stepName,
                            action: stepContent,
                            status: 'running'
                        })
                        this.streamingMessage.steps = [...this.currentSteps]
                        this.currentThinking = stepContent || stepName || 'Processando...'
                        this.streamingMessage.thinking = this.currentThinking
                    }
                    break

                case 'response':
                    if (event.data) {
                        const r = event.data
                        // Resposta final do MCP - só sobrescreve se ainda não tem conteúdo
                        if (!this.streamingMessage.content) {
                            this.streamingMessage.content = r.type === 'error' ? 'Erro ao processar.' : (r.message || '')
                        }
                        
                        // Captura metadata no debug
                        if (this.currentDebugInfo) {
                            this.currentDebugInfo.responseType = r.type
                            this.currentDebugInfo.sessionId = r.sessionId
                            if (r.metadata) {
                                this.currentDebugInfo.metadata = r.metadata
                                this.currentDebugInfo.toolsExecuted = r.metadata.toolsExecuted || []
                            }
                            if (r.toolParams) {
                                this.currentDebugInfo.toolParams = r.toolParams
                            }
                            if (r.toolCalls && r.toolCalls.length) {
                                this.currentDebugInfo.toolCalls = r.toolCalls
                            }
                            if (r.thoughts) {
                                r.thoughts.forEach(t => this.currentDebugInfo.thoughts.push(t))
                            }
                            if (r.data) {
                                const s = JSON.stringify(r.data)
                                this.currentDebugInfo.rawData = s.length < 2000 ? r.data : { _truncated: true, _size: s.length, _preview: s.substring(0, 500) + '...' }
                            }
                        }
                        
                        // Atualiza tools_executed se veio nos metadados
                        if (r.metadata && r.metadata.toolsExecuted) {
                            this.streamingMessage.tools_executed = r.metadata.toolsExecuted.length
                        }
                        
                        // Detecta se é uma confirmação (Human-in-the-Loop)
                        if (r.type === 'confirmation') {
                            this.$set(this.streamingMessage, 'isConfirmation', true)
                            this.$set(this.streamingMessage, 'confirmationAnswered', false)
                        }
                        
                        // Detecta se é uma seleção entre múltiplos resultados
                        const msg = r.message || ''
                        if (msg.includes('Qual deseja') || msg.includes('Responda com o número')) {
                            const selectionOptions = this.parseSelectionOptions(msg)
                            if (selectionOptions.length > 0) {
                                this.$set(this.streamingMessage, 'isSelection', true)
                                this.$set(this.streamingMessage, 'selectionOptions', selectionOptions)
                                this.$set(this.streamingMessage, 'selectionAnswered', false)
                            }
                        }
                    }
                    break

                // === Eventos legados (compatibilidade) ===
                case 'thinking':
                    this.currentThinking = event.message || ''
                    this.streamingMessage.thinking = this.currentThinking
                    break

                case 'step_start':
                    this.currentSteps.push({
                        step: event.step,
                        action: event.action,
                        status: 'running'
                    })
                    this.streamingMessage.steps = [...this.currentSteps]
                    break

                case 'step_complete': {
                    const stepIndex = this.currentSteps.findIndex(s => s.step === event.step)
                    if (stepIndex !== -1) {
                        this.currentSteps[stepIndex].status = event.success ? 'done' : 'error'
                        this.streamingMessage.steps = [...this.currentSteps]
                    }
                    break
                }

                case 'plan':
                    if (event.steps && event.steps.length > 0) {
                        this.streamingMessage.content = `**Plano de execução:** ${event.objetivo || ''}\n\n`
                        event.steps.forEach(step => {
                            this.streamingMessage.content += `• ${step}\n`
                        })
                        this.streamingMessage.content += '\n---\n\n'
                    }
                    break

                case 'tool_call':
                    // event.tool ja vem humanizado do backend (ex: "Listando clientes")
                    // Prefixo "Consultando:" removido pra evitar redundancia (ex: "Consultando: Consultando X")
                    this.currentThinking = `${event.tool}...`
                    this.streamingMessage.thinking = this.currentThinking
                    break

                case 'status':
                    // Status amigavel emitido pelo backend ("Pensando...",
                    // "Mapeando a consulta...", "Consultando o sistema...").
                    // Reusa o slot currentThinking pra renderizar igual tool_call.
                    this.currentThinking = event.message || ''
                    this.streamingMessage.thinking = this.currentThinking
                    break

                case 'tool_result':
                    this.streamingMessage.tools_executed = (this.streamingMessage.tools_executed || 0) + 1
                    break

                case 'content':
                    // Limpa steps visuais quando começa a receber conteúdo
                    if (this.currentSteps.length > 0) {
                        this.currentSteps = []
                        this.streamingMessage.steps = []
                    }
                    this.currentThinking = ''
                    this.streamingMessage.thinking = ''
                    this.streamingMessage.content += event.content || ''
                    this.scrollToBottom()
                    break

                case 'file': {
                    // Arquivo gerado pela skill — pode vir como URL absoluta (S3
                    // presigned direto) ou path relativo (/api/files/xxx no
                    // chat-agent). Detecta e monta corretamente.
                    if (!this.streamingMessage) break
                    if (!Array.isArray(this.streamingMessage.arquivos)) {
                        this.$set(this.streamingMessage, 'arquivos', [])
                    }
                    let downloadUrl = event.download_url || ''
                    if (!/^https?:\/\//i.test(downloadUrl)) {
                        const base = process.env.VUE_APP_AGENT_CHAT_URL || 'https://chat-agent.click.app'
                        const token = autenticacao.getToken ? autenticacao.getToken() : ''
                        const sep = downloadUrl.includes('?') ? '&' : '?'
                        downloadUrl = `${base}${downloadUrl}${sep}x_token_auth=${encodeURIComponent(token)}`
                    }
                    this.streamingMessage.arquivos.push({
                        fileId: event.file_id,
                        filename: event.filename || event.file_id,
                        kind: event.kind,
                        downloadUrl,
                    })
                    this.scrollToBottom()
                    break
                }

                case 'done': {
                    this.currentThinking = ''
                    this.currentSteps = []
                    
                    // Finaliza debug info
                    if (this.currentDebugInfo) {
                        this.currentDebugInfo.totalTime = Date.now() - this.currentDebugInfo.startTime
                        // Limita streamLog aos últimos 20 eventos
                        if (this.currentDebugInfo.streamLog.length > 20) {
                            this.currentDebugInfo.streamLog = this.currentDebugInfo.streamLog.slice(-20)
                        }
                        // Remove duplicatas de thoughts
                        const seen = new Set()
                        this.currentDebugInfo.thoughts = this.currentDebugInfo.thoughts.filter(t => {
                            const key = t.step + ':' + t.content
                            if (seen.has(key)) return false
                            seen.add(key)
                            return true
                        })
                    }
                    
                    if (this.streamingMessage) {
                        const toolsExecuted = Math.max(
                            this.streamingMessage.tools_executed || 0,
                            event.tools_executed || 0
                        )
                        
                        const chartAvailable = event.chart_available === true || event.chart_available === 'true' || event.chart_available === 1
                        const chartToolName = event.chart_tool_name || null
                        const chartToolData = event.chart_tool_data || null
                        
                        const msgIndex = this.mensagens.indexOf(this.streamingMessage)
                        if (msgIndex !== -1) {
                            const mensagemAtualizada = {
                                ...this.streamingMessage,
                                thinking: '',
                                steps: [],
                                isStreaming: false,
                                tools_executed: toolsExecuted,
                                chart_available: chartAvailable,
                                chart_tool_name: chartToolName,
                                chart_tool_data: chartToolData,
                                chart_generated: false,
                                charts: [],
                                arquivos: this.streamingMessage.arquivos || [],
                                // Salva debug info na mensagem
                                debugInfo: this.currentDebugInfo ? { ...this.currentDebugInfo } : null
                            }
                            
                            this.$set(this.mensagens, msgIndex, mensagemAtualizada)
                        }
                    }

                    // Atualiza widget de consumo apos resposta finalizada.
                    // Pequeno delay (300ms) pra dar tempo do chat-agent debitar
                    // tokens no intranet (debit_tokens roda em asyncio.create_task).
                    setTimeout(() => this.atualizarQuota(), 300)
                    break
                }

                case 'error':
                    this.streamingMessage.content = event.message || event.data?.message || 'Erro ao processar mensagem.'
                    // Salva debug info mesmo em erro
                    if (this.currentDebugInfo) {
                        this.currentDebugInfo.error = event.message || event.data
                        this.currentDebugInfo.totalTime = Date.now() - this.currentDebugInfo.startTime
                        this.$set(this.streamingMessage, 'debugInfo', { ...this.currentDebugInfo })
                    }
                    // Se tiver ação sugerida (como comprar tokens)
                    if (event.acao) {
                        this.streamingMessage.acao = event.acao
                    }
                    break
            }
        },
        cancelarStream() {
            if (this.abortController) {
                this.abortController.abort()
            }
        },
        /**
         * Monta o histórico de conversação para enviar ao MCP
         */
        getConversationHistory() {
            // Pega últimas 10 mensagens (5 pares user/assistant)
            const recentMessages = this.mensagens.slice(-10)
            return recentMessages
                .filter(m => m.role === 'user' || m.role === 'assistant')
                .filter(m => m.content && m.content.trim())
                .map(m => ({
                    role: m.role,
                    content: m.content
                }))
        },
        /**
         * Extrai opções de seleção de uma mensagem (múltiplos resultados)
         */
        parseSelectionOptions(message) {
            const options = []
            // Padrão: "1. Texto da opção" ou "1) Texto da opção"
            const regex = /(\d+)[.)\s]+([^\n]+)/g
            let match
            while ((match = regex.exec(message)) !== null) {
                const idx = parseInt(match[1])
                const text = match[2].trim()
                if (text && idx > 0) {
                    options.push({ index: idx, text })
                }
            }
            // Detecta opção "Todos" (0)
            if (message.toLowerCase().includes('todos') || message.includes('0)') || message.includes('0.')) {
                options.push({ index: 0, text: 'Aplicar a todos' })
            }
            return options
        },
        /**
         * Responde a uma confirmação (Human-in-the-Loop)
         */
        responderConfirmacao(msgIndex, resposta) {
            this.$set(this.mensagens[msgIndex], 'confirmationAnswered', true)
            this.$set(this.mensagens[msgIndex], 'confirmationResponse', resposta)
            
            // Envia a resposta como mensagem
            this.mensagemInput = resposta
            this.enviarMensagem(false)
        },
        /**
         * Responde a uma seleção entre múltiplos resultados
         */
        responderSelecao(msgIndex, opcao) {
            this.$set(this.mensagens[msgIndex], 'selectionAnswered', true)
            this.$set(this.mensagens[msgIndex], 'selectionResponse', String(opcao))
            
            // Envia o número da opção como mensagem
            this.mensagemInput = String(opcao)
            this.enviarMensagem(false)
        },
        // === Debug Modal Methods ===
        abrirDebugModal(msg) {
            if (!msg.debugInfo) return
            this.debugModalData = msg.debugInfo
            this.expandedLogs = {}
            this.expandedTools = {}
            this.expandedToolResponses = {}
            this.toolResponseExpanded = false
            this.debugModalVisible = true
        },
        fecharDebugModal() {
            this.debugModalVisible = false
            this.debugModalData = null
        },
        toggleLog(idx) {
            this.$set(this.expandedLogs, idx, !this.expandedLogs[idx])
        },
        toggleToolExpand(idx) {
            this.$set(this.expandedTools, idx, !this.expandedTools[idx])
        },
        toggleToolResponse(idx) {
            this.$set(this.expandedToolResponses, idx, !this.expandedToolResponses[idx])
        },
        getDataCount(data) {
            if (Array.isArray(data)) return data.length
            if (data && typeof data === 'object') {
                if (data._truncated) return data._preview ? '(truncado)' : '-'
                return Object.keys(data).length
            }
            return '-'
        },
        truncate(text, maxLen) {
            if (!text) return ''
            const str = String(text)
            return str.length > maxLen ? str.substring(0, maxLen) + '...' : str
        },
        formatParamValue(value) {
            if (value === null || value === undefined) return 'null'
            if (typeof value === 'object') return JSON.stringify(value)
            return String(value)
        },
        syntaxHighlight(json) {
            if (json === null || json === undefined) return ''
            if (typeof json !== 'string') {
                json = JSON.stringify(json, null, 2)
            }
            json = json.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
            return json.replace(/("(\\u[a-zA-Z0-9]{4}|\\[^u]|[^\\"])*"(\s*:)?|\b(true|false|null)\b|-?\d+(?:\.\d*)?(?:[eE][+-]?\d+)?)/g, (match) => {
                let cls = 'number'
                if (/^"/.test(match)) {
                    if (/:$/.test(match)) {
                        cls = 'key'
                    } else {
                        cls = 'string'
                    }
                } else if (/true|false/.test(match)) {
                    cls = 'boolean'
                } else if (/null/.test(match)) {
                    cls = 'null'
                }
                return '<span class="json-' + cls + '">' + match + '</span>'
            })
        },
        async enviarMensagemProgramatica(mensagem) {
            if (!mensagem || !this.sessaoToken) return
            
            this.mensagemInput = mensagem
            await this.enviarMensagem(false)
        },
        async gerarGrafico(msg) {
            if (!msg.chart_tool_name || !msg.chart_tool_data) return
            this.$set(msg, 'gerandoGrafico', true)
            try {
                const response = await this.$api.cPost('/ia/agent_chart', {
                    session_token: this.sessaoToken,
                    tool_name: msg.chart_tool_name,
                    tool_data: msg.chart_tool_data,
                    user_query: this.ultimaConsulta || ''
                })
                
                const dadosChart = response.dados || response.data || {}
                if (response && response.status === 'success' && dadosChart.charts) {
                    this.$set(msg, 'charts', dadosChart.charts)
                    this.$set(msg, 'chart_generated', true)
                    
                    this.$nextTick(() => {
                        this.renderizarGraficos(msg)
                    })
                } else {
                    this.$notify.error((response && response.message) || 'Não foi possível gerar o gráfico.')
                }
            } catch (error) {
                console.error('Erro ao gerar gráfico:', error)
                this.$notify.error('Erro ao gerar gráfico.')
            } finally {
                this.$set(msg, 'gerandoGrafico', false)
            }
        },
        
        verRespostaFerramenta(msg) {
            this.currentToolMessage = msg
            this.toolResponseData = {
                tool_name: msg.chart_tool_name || 'Ferramenta',
                success: true,
                data: msg.chart_tool_data,
                chart_available: msg.chart_available && !msg.chart_generated
            }
            this.$refs.modalToolResponse.show()
        },
        
        formatToolResponse(data) {
            try {
                return JSON.stringify(data, null, 2)
            } catch {
                return String(data)
            }
        },
        
        async gerarGraficoDoModal() {
            if (!this.currentToolMessage) return
            
            await this.gerarGrafico(this.currentToolMessage)
            
            if (this.currentToolMessage.chart_generated) {
                this.toolResponseData.chart_available = false
                this.$refs.modalToolResponse.hide()
            }
        },
        
        async copiarMensagem(msg) {
            try {
                await navigator.clipboard.writeText(msg.content)
                this.$set(msg, 'copiado', true)
                setTimeout(() => {
                    this.$set(msg, 'copiado', false)
                }, 2000)
            } catch (error) {
                console.error('Erro ao copiar:', error)
            }
        },
        
        carregarJsPDF() {
            return new Promise((resolve) => {
                if (window.jspdf) {
                    resolve()
                    return
                }
                
                if (!document.querySelector('script[src="/js/relatorio/jspdf.min.js"]')) {
                    const script = document.createElement('script')
                    script.setAttribute('src', '/js/relatorio/jspdf.min.js')
                    script.onload = () => {
                        const script2 = document.createElement('script')
                        script2.setAttribute('src', '/js/relatorio/autotable.min.js')
                        script2.onload = () => resolve()
                        document.head.appendChild(script2)
                    }
                    document.head.appendChild(script)
                } else {
                    resolve()
                }
            })
        },
        
        dividirEmSecoes(conteudo) {
            const linhas = conteudo.split('\n')
            const secoes = []
            let secaoAtual = { titulo: '', texto: '', tabela: null }
            let emTabela = false
            let linhasTabela = []
            
            for (let i = 0; i < linhas.length; i++) {
                const linha = linhas[i]
                const linhaLimpa = linha.trim()
                
                // Detecta header (### ou ####)
                const matchHeader = linhaLimpa.match(/^#{1,6}\s*(.+)$/)
                if (matchHeader) {
                    // Salva seção anterior se existir
                    if (secaoAtual.titulo || secaoAtual.texto || secaoAtual.tabela) {
                        if (emTabela && linhasTabela.length > 0) {
                            secaoAtual.tabela = this.parsearTabela(linhasTabela)
                        }
                        secoes.push(secaoAtual)
                    }
                    // Inicia nova seção
                    secaoAtual = { 
                        titulo: matchHeader[1].replace(/\*+/g, '').trim(), 
                        texto: '', 
                        tabela: null 
                    }
                    emTabela = false
                    linhasTabela = []
                    continue
                }
                
                // Detecta início de tabela (linha que começa com |)
                if (linhaLimpa.startsWith('|')) {
                    emTabela = true
                    // Ignora linha separadora |---|---|
                    if (!/^[\s|:-]+$/.test(linhaLimpa)) {
                        linhasTabela.push(linhaLimpa)
                    }
                    continue
                }
                
                // Se estava em tabela e encontrou linha que não é tabela
                if (emTabela && !linhaLimpa.startsWith('|') && linhaLimpa !== '') {
                    if (linhasTabela.length > 0) {
                        secaoAtual.tabela = this.parsearTabela(linhasTabela)
                    }
                    emTabela = false
                    linhasTabela = []
                }
                
                // Adiciona texto (se não estiver em tabela)
                if (!emTabela && linhaLimpa) {
                    const textoLimpo = linhaLimpa
                        .replace(/\*+/g, '')
                        .replace(/\[([^\]]+)\]\([^)]*\)/g, '$1')
                    secaoAtual.texto += textoLimpo + '\n'
                }
            }
            
            // Salva última seção
            if (emTabela && linhasTabela.length > 0) {
                secaoAtual.tabela = this.parsearTabela(linhasTabela)
            }
            if (secaoAtual.titulo || secaoAtual.texto || secaoAtual.tabela) {
                secoes.push(secaoAtual)
            }
            
            // Se não encontrou seções, cria uma com todo o conteúdo
            if (secoes.length === 0) {
                secoes.push({
                    titulo: '',
                    texto: conteudo.replace(/\*+/g, '').replace(/[#]+/g, ''),
                    tabela: null
                })
            }
            
            return secoes
        },
        
        parsearTabela(linhasTabela) {
            if (linhasTabela.length < 2) return null
            
            const headers = linhasTabela[0]
                .split('|')
                .filter(c => c.trim())
                .map(c => c.replace(/\*+/g, '').trim())
            
            const dados = linhasTabela.slice(1).map(linha => 
                linha.split('|')
                    .filter(c => c.trim())
                    .map(c => c.replace(/\*+/g, '').trim())
            )
            
            return { headers, dados }
        },
        
        normalizarTexto(texto) {
            // Remove acentos e caracteres especiais que o jsPDF não suporta
            const mapaAcentos = {
                'á': 'a', 'à': 'a', 'ã': 'a', 'â': 'a', 'ä': 'a',
                'é': 'e', 'è': 'e', 'ê': 'e', 'ë': 'e',
                'í': 'i', 'ì': 'i', 'î': 'i', 'ï': 'i',
                'ó': 'o', 'ò': 'o', 'õ': 'o', 'ô': 'o', 'ö': 'o',
                'ú': 'u', 'ù': 'u', 'û': 'u', 'ü': 'u',
                'ç': 'c', 'ñ': 'n',
                'Á': 'A', 'À': 'A', 'Ã': 'A', 'Â': 'A', 'Ä': 'A',
                'É': 'E', 'È': 'E', 'Ê': 'E', 'Ë': 'E',
                'Í': 'I', 'Ì': 'I', 'Î': 'I', 'Ï': 'I',
                'Ó': 'O', 'Ò': 'O', 'Õ': 'O', 'Ô': 'O', 'Ö': 'O',
                'Ú': 'U', 'Ù': 'U', 'Û': 'U', 'Ü': 'U',
                'Ç': 'C', 'Ñ': 'N'
            }
            
            let resultado = texto
            for (const [acento, semAcento] of Object.entries(mapaAcentos)) {
                resultado = resultado.split(acento).join(semAcento)
            }
            
            // Remove emojis e caracteres especiais
            resultado = resultado
                .replace(/[\u{1F300}-\u{1F9FF}]/gu, '') // Emojis
                .replace(/[\u{2700}-\u{27BF}]/gu, '')   // Dingbats
                .replace(/[\u{2000}-\u{206F}]/gu, ' ')  // Espaços especiais
                .replace(/[\u{2010}-\u{2015}]/gu, '-')  // Hífens especiais
                .replace(/[\u{2018}-\u{201F}]/gu, '"')  // Aspas especiais
                .replace(/[\u{2022}]/gu, '-')          // Bullet point
                .replace(/[\u{2026}]/gu, '...')        // Reticências
                .replace(/[^\x20-\x7E\n\r\t]/g, '')
            
            return resultado
        },
        
        async exportarPDF(msg) {
            this.$set(msg, 'exportandoPdf', true)
            
            try {
                await this.carregarJsPDF()
                
                // eslint-disable-next-line no-undef
                const { jsPDF } = window.jspdf
                const doc = new jsPDF('p', 'mm', 'a4')
                
                const titulo = this.normalizarTexto('Relatorio - Assistente IA')
                const dataGeracao = this.$moment ? this.$moment().format('DD/MM/YYYY [as] HH:mm') : new Date().toLocaleString('pt-BR')
                
                doc.setFontSize(16)
                doc.setTextColor(36, 61, 75)
                doc.text(titulo, 14, 20)
                
                doc.setFontSize(10)
                doc.setTextColor(100, 100, 100)
                doc.text(`Gerado em ${dataGeracao}`, 14, 28)
                
                doc.setDrawColor(200, 200, 200)
                doc.line(14, 32, 196, 32)
                
                const conteudo = this.normalizarTexto(msg.content || '')
                
                const secoes = this.dividirEmSecoes(conteudo)
                let posY = 40
                
                for (const secao of secoes) {
                    if (posY > 250) {
                        doc.addPage()
                        posY = 20
                    }
                    
                    if (secao.titulo) {
                        doc.setFontSize(12)
                        doc.setTextColor(36, 61, 75)
                        doc.text(secao.titulo, 14, posY)
                        posY += 8
                    }
                    
                    if (secao.tabela && secao.tabela.headers.length > 0) {
                        doc.autoTable({
                            head: [secao.tabela.headers],
                            body: secao.tabela.dados,
                            startY: posY,
                            theme: 'striped',
                            headStyles: {
                                fillColor: [36, 61, 75],
                                textColor: [255, 255, 255],
                                fontStyle: 'bold',
                                fontSize: 8,
                                halign: 'center'
                            },
                            bodyStyles: {
                                fontSize: 7,
                                textColor: [80, 80, 80],
                                halign: 'center'
                            },
                            alternateRowStyles: {
                                fillColor: [245, 247, 250]
                            },
                            styles: {
                                lineColor: [220, 220, 220],
                                lineWidth: 0.1,
                                cellPadding: 2
                            },
                            margin: { left: 14, right: 14 },
                            tableWidth: 'auto'
                        })
                        posY = doc.lastAutoTable.finalY + 10
                    } else if (secao.texto) {
                        doc.setFontSize(10)
                        doc.setTextColor(50, 50, 50)
                        const linhasTexto = doc.splitTextToSize(secao.texto, 180)
                        
                        for (const linha of linhasTexto) {
                            if (posY > 275) {
                                doc.addPage()
                                posY = 20
                            }
                            doc.text(linha, 14, posY)
                            posY += 5
                        }
                        posY += 5
                    }
                }
                
                const pageCount = doc.internal.getNumberOfPages()
                for (let i = 1; i <= pageCount; i++) {
                    doc.setPage(i)
                    doc.setFontSize(8)
                    doc.setTextColor(150, 150, 150)
                    doc.text(`Página ${i} de ${pageCount}`, 14, 287)
                    doc.text('Gerado pelo Assistente IA', 196, 287, { align: 'right' })
                }
                
                const nomeArquivo = `relatorio-ia-${Date.now()}.pdf`
                doc.save(nomeArquivo)
                
                this.$notify.success('PDF exportado com sucesso!')
            } catch (error) {
                console.error('Erro ao exportar PDF:', error)
                this.$notify.error('Erro ao gerar PDF')
            } finally {
                this.$set(msg, 'exportandoPdf', false)
            }
        },
        
        async avaliarResposta(msg, tipo) {
            if (msg.avaliacao || msg.enviandoAvaliacao) return
            this.$set(msg, 'enviandoAvaliacao', true)

            const idxAtual = this.mensagens.indexOf(msg)
            const perguntaUser = idxAtual > 0
                ? (this.mensagens[idxAtual - 1].content || '').slice(0, 5000)
                : ''

            let correcao = null
            if (tipo === 'negativo') {
                const resp = window.prompt(
                    'Qual era a resposta certa? (opcional — ajuda o time a melhorar)',
                    ''
                )
                if (resp && resp.trim()) correcao = resp.trim().slice(0, 10000)
            }

            try {
                await this.apiIA.cPost('/v1/feedback', {
                    feedback_tipo: tipo,
                    pergunta: perguntaUser,
                    resposta: (msg.content || '').slice(0, 20000),
                    correcao,
                    session_id: this.sessaoToken,
                    message_id: msg.id || null,
                    tool_name: msg.chart_tool_name || null,
                })
                this.$set(msg, 'avaliacao', tipo)
            } catch (err) {
                console.warn('Falha ao enviar feedback ao intranet:', err)
                if (this.$notify) {
                    this.$notify.error('Não foi possível registrar sua avaliação. Tente de novo.')
                }
            } finally {
                this.$set(msg, 'enviandoAvaliacao', false)
            }
        },
        renderizarGraficos(msg) {
            const msgIndex = this.mensagens.indexOf(msg)
            if (msgIndex === -1) return
            
            msg.charts.forEach((chart, chartIdx) => {
                const refKey = `chart-${msgIndex}-${chartIdx}`
                const containers = this.$refs[refKey]
                const wrapper = Array.isArray(containers) ? containers[0] : containers
                
                if (wrapper && chart.config) {
                    const container = wrapper.querySelector('.chart-content') || wrapper
                    
                    try {
                        Highcharts.chart(container, chart.config)
                    } catch (error) {
                        console.error('Erro ao renderizar gráfico:', error)
                    }
                }
            })
            
            this.scrollToBottom()
        },
        expandTable(buttonElement) {
            const tableWrapper = buttonElement.closest('.table-wrapper')
            if (tableWrapper) {
                const tableContainer = tableWrapper.querySelector('.table-responsive')
                if (tableContainer) {
                    this.$refs.modalTabela.abrir(tableContainer.innerHTML)
                }
            }
        },
        enviarSugestao(sugestao) {
            this.mensagemInput = typeof sugestao === 'object' ? sugestao.command : sugestao
            this.enviarMensagem(false)
        },
        // extrairOpcoes e selecionarOpcao removidos - botões de opções desabilitados
        abrirBibliotecaPrompts() {
            this.$refs.modalBibliotecaPrompts.abrir()
        },

        abrirContextoModal() {
            this.$refs.modalContextoSessao.abrir(this.sessaoToken)
        },
        
        toggleExpandido() {
            this.chatExpandido = !this.chatExpandido
            this.salvarExpandido()
        },

        abrirConfiguracoesIa() {
            window.location.href = '/ia'
        },
        
        salvarExpandido() {
            localStorage.setItem('chat-ia-expandido', this.chatExpandido)
        },
        
        favoritarPrompt(msg) {
            // Se já é favorito, apenas remove
            if (msg.favorito) {
                this.$set(msg, 'favorito', false)
                this.$notify.info('Modelo removido dos favoritos')
                return
            }
            
            this.$refs.modalPromptFavoritos.abrir(msg)
        },
        
        onSalvarFavorito(dados) {
            // Marca a mensagem como favorita
            if (dados.mensagemOriginal) {
                this.$set(dados.mensagemOriginal, 'favorito', true)
            }
            
            // TODO: Salvar no backend
            this.$notify.success('Modelo salvo nos favoritos!')
        },
        
        usarPromptDaBiblioteca(comando) {
            this.mensagemInput = comando
            // Foca no input para o usuário poder editar antes de enviar
            this.$nextTick(() => {
                const input = this.$refs.inputArea
                if (input) {
                    input.focus()
                    // Move cursor para o final do texto
                    input.setSelectionRange(comando.length, comando.length)
                }
            })
        },
        
        async limparChat() {
            const confirmacao = await this.$dialog.confirm(
                'Deseja realmente limpar a conversa e iniciar uma nova sessão?',
                { icon: 'text-warning glyphicon glyphicon-warning-sign' }
            )
            
            if (!confirmacao) return
            
            if (this.sessaoToken) {
                try {
                    await this.$api.cPost('/ia/agent_close', {
                        session_token: this.sessaoToken
                    })
                } catch (e) {
                    console.warn('Erro ao fechar sessão no backend:', e)
                }
            }
            
            this.limparSessaoLocal()
            await this.iniciarSessao()
        },
        scrollToBottom() {
            // Só faz scroll se auto-scroll estiver habilitado
            if (!this.autoScrollEnabled && this.isStreaming) {
                return
            }
            this.$nextTick(() => {
                if (this.$refs.chatMessages) {
                    this.$refs.chatMessages.scrollTop = this.$refs.chatMessages.scrollHeight
                }
            })
        },
        scrollToBottomAndEnable() {
            // Força scroll e reativa auto-scroll
            this.autoScrollEnabled = true
            this.showScrollToBottom = false
            this.$nextTick(() => {
                if (this.$refs.chatMessages) {
                    this.$refs.chatMessages.scrollTop = this.$refs.chatMessages.scrollHeight
                }
            })
        },
        formatNumber(num) {
            if (num === null || num === undefined) return '0'
            return num.toLocaleString('pt-BR')
        },
        getQuotaClass(porcentagem) {
            if (porcentagem >= 90) return 'danger'
            if (porcentagem >= 70) return 'warning'
            return 'success'
        },
        async atualizarQuota() {
            // Chama o intranet-agent-click direto (mesmo endpoint usado por /ia/inicio).
            // CakePHP /ia/meu_consumo era endpoint legado que retornava sempre 0.
            try {
                // cGet = get sem loading global (pra refresh discreto do widget)
                const response = await this.apiIA.cGet('/v1/quotas/meu-consumo')
                const dados = response && response.code === 200 ? response.data : null
                const q = dados && dados.quota
                if (q) {
                    this.quota = q
                    if (this.persistirSessao) {
                        this.salvarSessao()
                    }
                }
            } catch (error) {
                console.warn('Erro ao atualizar quota:', error)
            }
        },
        autoResize() {
            const textarea = this.$refs.inputArea
            if (textarea) {
                textarea.style.height = 'auto'
                textarea.style.height = Math.min(textarea.scrollHeight, 100) + 'px'
            }
        },
        resetTextarea() {
            const textarea = this.$refs.inputArea
            if (textarea) {
                textarea.style.height = 'auto'
            }
        },
        formatarMensagem(content) {
            if (!content) return ''
            
            let formatted = content
            
            // Remove blocos de botões de opções que possam vir no conteúdo
            formatted = formatted.replace(/<div[^>]*class="[^"]*message-options[^"]*"[^>]*>[\s\S]*?<\/div>/gi, '')
            formatted = formatted.replace(/<button[^>]*class="[^"]*option-btn[^"]*"[^>]*>[\s\S]*?<\/button>/gi, '')
            
            formatted = formatted.replace(/```(\w*)\n?([\s\S]*?)```/g, (match, lang, code) => {
                return `<pre class="code-block${lang ? ' lang-' + lang : ''}"><code>${this.escapeHtml(code.trim())}</code></pre>`
            })
            
            formatted = formatted.replace(/\\\[([\s\S]*?)\\\]/g, (match, formula) => {
                return `<div class="math-block">${this.formatarFormula(formula.trim())}</div>`
            })
            formatted = formatted.replace(/\\\((.*?)\\\)/g, (match, formula) => {
                return `<span class="math-inline">${this.formatarFormula(formula.trim())}</span>`
            })
            
            formatted = this.processarTabelas(formatted)
            
            formatted = formatted.replace(/^#### (.+)$/gm, '<h6 class="md-header md-h4">$1</h6>')
            formatted = formatted.replace(/^### (.+)$/gm, '<h5 class="md-header md-h3">$1</h5>')
            formatted = formatted.replace(/^## (.+)$/gm, '<h4 class="md-header md-h2">$1</h4>')
            formatted = formatted.replace(/^# (.+)$/gm, '<h3 class="md-header md-h1">$1</h3>')
            
            // 5. Processar listas - processa blocos inteiros
            formatted = this.processarListas(formatted)
            
            // 6. Processar formatação inline
            formatted = formatted.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
            formatted = formatted.replace(/\*([^*]+)\*/g, '<em>$1</em>')
            formatted = formatted.replace(/`([^`]+)`/g, '<code class="inline-code">$1</code>')
            formatted = formatted.replace(/~~([^~]+)~~/g, '<del>$1</del>')
            
            // 7. Processar links
            formatted = formatted.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" class="link-agente" target="_blank">$1</a>')
            
            // 8. Processar links internos do sistema
            formatted = formatted.replace(
                /(?<!href="|">)(\/(?:clientes|fornecedores|financeiro\/movimentacoes_financeiras|vendas_produtos|produtos|compras|notas_fiscais)\/(?:visualizar|visualizar_pagamento|visualizar_recebimento)\/\d+)/g,
                '<a href="$1" class="link-agente">$1</a>'
            )
            
            // 9. Processar quebras de linha (preservar parágrafos)
            formatted = formatted.replace(/\n\n/g, '</p><p class="md-paragraph">')
            // Não adicionar <br> antes/depois de elementos de bloco
            formatted = formatted.replace(/\n+(?=<(?:ul|ol|table|div|h[1-6]|blockquote|pre))/gi, '')
            formatted = formatted.replace(/(<\/(?:ul|ol|table|div|h[1-6]|blockquote|pre)[^>]*>)\n+/gi, '$1')
            formatted = formatted.replace(/\n/g, '<br>')
            // Limpar <br> entre elementos de bloco (pós-processamento)
            formatted = formatted.replace(/<br>+(?=<(?:ul|ol|table|div|h[1-6]|blockquote|pre))/gi, '')
            formatted = formatted.replace(/(<\/(?:ul|ol|table|div|h[1-6]|blockquote|pre)[^>]*>)<br>+/gi, '$1')
            formatted = formatted.replace(/→/g, '<span class="text-primary">→</span>')
            
            // Envolver em parágrafo se não começar com tag
            if (!formatted.startsWith('<')) {
                formatted = '<p class="md-paragraph">' + formatted + '</p>'
            }
            
            return formatted
        },
        escapeHtml(text) {
            const map = {
                '&': '&amp;',
                '<': '&lt;',
                '>': '&gt;',
                '"': '&quot;',
                "'": '&#039;'
            }
            return text.replace(/[&<>"']/g, m => map[m])
        },
        formatarFormula(formula) {
            // Formata fórmulas matemáticas de forma legível
            return formula
                .replace(/\\text\{([^}]+)\}/g, '<span class="formula-text">$1</span>')
                .replace(/\\frac\{([^}]+)\}\{([^}]+)\}/g, '<span class="formula-frac"><span class="frac-num">$1</span><span class="frac-line"></span><span class="frac-den">$2</span></span>')
                .replace(/\\approx/g, '≈')
                .replace(/\\times/g, '×')
                .replace(/\\div/g, '÷')
                .replace(/\\pm/g, '±')
                .replace(/\\leq/g, '≤')
                .replace(/\\geq/g, '≥')
                .replace(/\\neq/g, '≠')
                .replace(/\\sum/g, '∑')
                .replace(/\\prod/g, '∏')
                .replace(/\\infty/g, '∞')
                .replace(/\\sqrt\{([^}]+)\}/g, '√($1)')
                .replace(/\^(\d+)/g, '<sup>$1</sup>')
                .replace(/_(\d+)/g, '<sub>$1</sub>')
        },
        processarListas(content) {
            // Processa blocos de listas não ordenadas (- item ou * item)
            const ulRegex = /(?:^|\n)((?:[\t ]*[-*] .+(?:\n|$))+)/g
            content = content.replace(ulRegex, (match, listBlock) => {
                const items = listBlock.trim().split('\n').map(line => {
                    const itemMatch = line.match(/^[\t ]*[-*] (.+)$/)
                    if (itemMatch) {
                        return `<li>${itemMatch[1]}</li>`
                    }
                    return ''
                }).filter(item => item).join('')
                return `<ul class="md-list">${items}</ul>`
            })
            
            // Processa blocos de listas ordenadas (1. item)
            const olRegex = /(?:^|\n)((?:[\t ]*\d+\. .+(?:\n|$))+)/g
            content = content.replace(olRegex, (match, listBlock) => {
                const items = listBlock.trim().split('\n').map(line => {
                    const itemMatch = line.match(/^[\t ]*\d+\. (.+)$/)
                    if (itemMatch) {
                        return `<li>${itemMatch[1]}</li>`
                    }
                    return ''
                }).filter(item => item).join('')
                return `<ol class="md-list-ordered">${items}</ol>`
            })
            
            return content
        },
        processarTabelas(content) {
            // Detecta e processa tabelas Markdown
            const tableRegex = /\|(.+)\|\n\|[-:| ]+\|\n((?:\|.+\|\n?)+)/g
            
            return content.replace(tableRegex, (match, headerLine, bodyLines) => {
                const headers = headerLine.split('|').map(h => h.trim()).filter(h => h)
                const rows = bodyLines.trim().split('\n').map(row => 
                    row.split('|').map(cell => cell.trim()).filter(cell => cell)
                )
                
                let table = `<div class="table-wrapper">`
                table += `<div class="table-actions"><button class="btn btn-sm btn-outline-secondary btn-expand-table" onclick="window.expandTable(this)"><i class="fa fa-expand"></i> Expandir</button></div>`
                table += `<div class="table-responsive"><table class="md-table table table-sm table-bordered">`
                table += '<thead><tr>'
                headers.forEach(h => { table += `<th>${h}</th>` })
                table += '</tr></thead><tbody>'
                rows.forEach(row => {
                    table += '<tr>'
                    row.forEach(cell => { table += `<td>${cell}</td>` })
                    table += '</tr>'
                })
                table += '</tbody></table></div></div>'
                
                return table
            })
        },
        filtrarErroTecnico(mensagem) {
            if (!mensagem) return 'Desculpe, ocorreu um erro ao processar sua mensagem.'
            
            const padroesTecnicos = [
                /SQLSTATE\[.*?\]/i,
                /Access denied for user/i,
                /Connection: mysql/i,
                /Database:/i,
                /Host:/i,
                /Port:/i,
                /using password/i,
                /SQL:/i,
                /Exception/i,
                /Error.*?Stack trace/i,
                /Fatal error/i,
                /Warning:/i,
                /Notice:/i,
                /Undefined/i,
                /Call to undefined/i
            ]
            
            for (const padrao of padroesTecnicos) {
                if (padrao.test(mensagem)) {
                    console.error('Erro técnico filtrado:', mensagem)
                    return 'Desculpe, estou com dificuldades técnicas. Tente novamente.'
                }
            }
            
            return mensagem
        }
    }
}
</script>

<style scoped>
.chat-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px 16px;
    background: #fff;
    border-bottom: 1px solid #e9ecef;
}

.header-info {
    display: flex;
    align-items: center;
    gap: 10px;
}

.avatar-ia {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background: #f1f3f4;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #5f6368;
    font-size: 1.1rem;
}

.header-text {
    display: flex;
    flex-direction: column;
}

.header-text strong {
    font-size: 0.95rem;
    color: #202124;
}

.header-text small {
    font-size: 0.75rem;
    color: #5f6368;
    display: flex;
    align-items: center;
}

.status-dot {
    display: inline-block;
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: #34a853;
    margin-right: 5px;
}

.status-dot.offline {
    background: #fbbc05;
}

.session-id {
    opacity: 0.7;
    font-size: 0.7rem;
}

.header-actions {
    display: flex;
    align-items: center;
    gap: 8px;
}
.header-icon-btn {
    cursor: pointer;
    color: #666;
    padding: 4px 8px;
    transition: color 0.2s;
}

.header-icon-btn:hover {
    color: rgb(36, 61, 75);
}

.header-actions .tira-sombra .dropdown-menu {
    min-width: 200px;
    box-shadow: 0 4px 12px rgba(0,0,0,0.15);
    border: 1px solid #e8eaed;
    border-radius: 8px;
}

.header-actions .tira-sombra .dropdown-item {
    padding: 10px 16px;
    font-size: 0.875rem;
}

.header-actions .tira-sombra .dropdown-item:hover {
    background: rgba(36, 61, 75, 0.1);
}

.header-actions .tira-sombra .dropdown-header-custom {
    padding: 0 5px;
    background: transparent;
}

.header-actions .tira-sombra .dropdown-header-text {
    font-size: 0.75rem;
    font-weight: 700;
    color: #6c757d;
    text-transform: uppercase;
    letter-spacing: 0.5px;
}

.header-actions .tira-sombra .dropdown-item i {
    color: #6c757d;
    width: 16px;
    text-align: center;
}

/* Quota no dropdown */
.quota-dropdown-info .b-dropdown-text{
    padding: 0 22px !important;
}

.quota-mini {
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.quota-mini-label {
    font-size: 0.8rem;
    color: #495057;
}

.quota-mini-bar {
    height: 4px;
    background: #e9ecef;
    border-radius: 2px;
    overflow: hidden;
}

.quota-mini-fill {
    height: 100%;
    border-radius: 2px;
    transition: width 0.3s ease;
}

.quota-mini-fill.success {
    background: #28a745;
}

.quota-mini-fill.warning {
    background: #ffc107;
}

.quota-mini-fill.danger {
    background: #dc3545;
}

.chat-messages {
    flex: 1;
    overflow-y: auto;
    padding: 16px;
    background: #f8f9fa;
    height: calc(100vh - 200px);
}

.loading-history,
.load-more-hint {
    margin-bottom: 10px;
    opacity: 0.7;
}

.load-more-hint {
    cursor: default;
}

.message-group {
    display: contents; /* Não afeta o layout, apenas agrupa os elementos */
}

.message {
    display: flex;
    margin-bottom: 12px;
    animation: fadeIn 0.2s ease;
}

@keyframes fadeIn {
    from { opacity: 0; transform: translateY(5px); }
    to { opacity: 1; transform: translateY(0); }
}

.message-user {
    flex-direction: row-reverse;
}

.message-avatar {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    font-size: 0.85rem;
}

.message-user .message-avatar {
    background: rgb(36, 61, 75);
    color: white;
    margin-left: 8px;
}

.message-assistant .message-avatar {
    background: #f1f3f4;
    color: #5f6368;
    margin-right: 8px;
}

.message-content {
    max-width: 85%;
    position: relative;
}

/* Menu de ações (3 pontinhos) */
/* Modal de resposta da ferramenta */
.tool-response-container {
    background: #f8f9fa;
    border-radius: 8px;
    padding: 16px;
}

.tool-response-json {
    background: #1e1e1e;
    color: #d4d4d4;
    padding: 16px;
    border-radius: 8px;
    font-size: 12px;
    max-height: 400px;
    overflow: auto;
    white-space: pre-wrap;
    word-wrap: break-word;
}

/* Modal de favoritar modelo */


.message-text {
    padding: 10px 14px;
    border-radius: 12px;
    line-height: 1.5;
    font-size: 0.9rem;
}

.message-user .message-text {
    background: rgb(36, 61, 75);
    color: white;
    border-bottom-right-radius: 4px;
}

.message-assistant .message-text {
    background: #fff;
    color: #202124;
    border: 1px solid #e8eaed;
    border-bottom-left-radius: 4px;
}

/* Anexos (arquivos gerados por skills) */
.arquivos-anexos {
    display: flex;
    flex-direction: column;
    gap: 6px;
}
.arquivo-link {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 8px 12px;
    background: #f8f9fa;
    border: 1px solid #e8eaed;
    border-radius: 8px;
    color: #202124;
    text-decoration: none;
    font-size: 14px;
    transition: background 0.15s, border-color 0.15s;
}
.arquivo-link:hover {
    background: #eff6ff;
    border-color: #93c5fd;
    color: #1e40af;
    text-decoration: none;
}
.arquivo-link .arquivo-nome {
    flex: 1;
    word-break: break-all;
}

/* Efeito de streaming/digitando */
.message-streaming .message-text::after {
    content: '▋';
    animation: blink 1s infinite;
    color: rgb(36, 61, 75);
}

@keyframes blink {
    0%, 50% { opacity: 1; }
    51%, 100% { opacity: 0; }
}

.thinking-indicator {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 12px;
    background: #f8f9fa;
    border-radius: 8px;
    margin-bottom: 8px;
    font-size: 0.85rem;
    color: #5f6368;
}

.thinking-indicator i {
    animation: spin 1s linear infinite;
}

@keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
}

.thinking-steps {
    margin-top: 8px;
    padding-left: 12px;
    border-left: 2px solid #e8eaed;
}

.thinking-step {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 4px 0;
    font-size: 0.8rem;
    color: #5f6368;
}

.thinking-step.running {
    color: rgb(36, 61, 75);
}

.thinking-step.done {
    color: #34a853;
}

.thinking-step.error {
    color: #ea4335;
}

.thinking-step-group {
    margin-bottom: 4px;
}

.step-category {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 4px 0;
    font-size: 0.8rem;
    color: #5f6368;
}

.step-category i.fa-spinner {
    color: rgb(36, 61, 75);
}

.step-category i.fa-check {
    color: #34a853;
}

.step-category i.fa-times {
    color: #ea4335;
}

.step-detail {
    padding-left: 20px;
    font-size: 0.75rem;
    color: #80868b;
    position: relative;
}

.step-detail::before {
    content: "└─";
    position: absolute;
    left: 4px;
    color: #dadce0;
}

.message-tools {
    margin-top: 4px;
    padding-left: 14px;
}

.chart-available-indicator {
    margin-top: 4px;
}

.chart-available-indicator .btn {
    font-size: 12px;
    padding: 4px 12px;
    border-radius: 16px;
    transition: all 0.2s ease;
}

.chart-available-indicator .btn:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 2px 6px rgba(0,0,0,0.15);
}

/* Barra de ações no final da mensagem */
.message-actions-bar {
    display: flex;
    align-items: center;
    gap: 4px;
    padding-top: 6px;
}

.message-actions-bar .action-btn {
    background: transparent;
    border: none;
    color: #9aa0a6;
    padding: 4px 8px;
    border-radius: 4px;
    cursor: pointer;
    font-size: 14px;
    transition: all 0.15s ease;
    line-height: 1;
}

.message-actions-bar .action-btn:hover:not(:disabled) {
    background: rgba(0,0,0,0.05);
    color: #5f6368;
}

.message-actions-bar .action-btn:disabled {
    cursor: default;
}

.message-actions-bar .action-btn.active {
    color: rgb(36, 61, 75);
}

.message-actions-bar .action-btn.active .fa-thumbs-o-up {
    color: #4caf50;
}

.message-actions-bar .action-btn.active .fa-thumbs-o-down {
    color: #f44336;
}

.message-actions-bar .action-btn.active .fa-star {
    color: #ffc107;
}

/* Ações do usuário - alinha à esquerda */
.message-user .message-actions-bar.user-actions {
    justify-content: flex-end;
}

.message-actions-bar .action-btn .fa-file-pdf-o {
    color: #e53935;
}

.message-actions-bar .action-btn:hover .fa-file-pdf-o {
    color: #c62828;
}

.message-action {
    padding-top: 10px;
}

.message-action .btn {
    border-radius: 20px;
    font-size: 0.85rem;
}

.message-chart-action {
    padding-top: 8px;
}

.charts-container {
    width: 100%;
    margin-top: 12px;
    margin-bottom: 8px;
    padding-left: 48px; /* Alinha com o balão da mensagem */
}

.chart-wrapper {
    background: #fff;
    border: 1px solid #e8eaed;
    border-radius: 8px;
    padding: 16px;
    margin-bottom: 8px;
    box-shadow: 0 1px 3px rgba(0,0,0,0.08);
}

.chart-wrapper:last-child {
    margin-bottom: 0;
}

.chart-content {
    min-height: 250px;
    width: 100%;
}

/* Sugestões Collapse */
.chat-suggestions-wrapper {
    background: #fafbfc;
    border-top: 1px solid #e9ecef;
}

.suggestions-toggle {
    width: 100%;
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 16px;
    background: transparent;
    border: none;
    cursor: pointer;
    font-size: 0.8rem;
    color: #5f6368;
    transition: all 0.2s ease;
}

.suggestions-toggle:hover {
    background: #f1f3f4;
}

.suggestions-toggle.active {
    color: rgb(36, 61, 75);
}

.suggestions-header {
    padding: 10px 16px;
    font-size: 0.8rem;
    color: #5f6368;
    font-weight: 500;
}

.suggestions-toggle .badge {
    background: #e8f0fe;
    color: rgb(36, 61, 75);
    font-size: 0.7rem;
    padding: 2px 6px;
    border-radius: 10px;
    font-weight: 500;
}

.suggestions-toggle .toggle-icon {
    margin-left: auto;
    font-size: 0.7rem;
    transition: transform 0.3s ease;
}

.suggestions-toggle .toggle-icon.rotated {
    transform: rotate(180deg);
}

.suggestions-content {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    padding: 0 16px 12px 16px;
}

.suggestion-chip {
    display: inline-flex;
    align-items: center;
    padding: 8px 14px;
    background: #fff;
    border: 1px solid #dadce0;
    border-radius: 20px;
    font-size: 0.8rem;
    color: #3c4043;
    cursor: pointer;
    transition: all 0.2s ease;
}

.suggestion-chip:hover {
    background: #e8f0fe;
    border-color: rgb(36, 61, 75);
    color: rgb(36, 61, 75);
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(26, 115, 232, 0.15);
}

.chat-input-container {
    padding: 12px 16px;
    background: #fff;
    border-top: 1px solid #e9ecef;
}

.input-wrapper {
    display: flex;
    align-items: flex-end;
    gap: 8px;
    background: #f1f3f4;
    border-radius: 20px;
    padding: 6px 6px 6px 16px;
}

.input-wrapper:focus-within {
    background: #fff;
    box-shadow: 0 1px 3px rgba(0,0,0,0.1);
    border: 1px solid rgb(36, 61, 75);
}

.input-wrapper textarea {
    flex: 1;
    border: none;
    background: transparent;
    resize: none;
    font-size: 0.9rem;
    line-height: 1.4;
    padding: 6px 0;
    max-height: 100px;
    outline: none;
}

.input-wrapper textarea::placeholder {
    color: #5f6368;
}

.btn-input-icon {
    width: 32px;
    height: 32px;
    min-width: 32px;
    border-radius: 50%;
    padding: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    background: transparent;
    border: none;
    color: #5f6368;
    cursor: pointer;
    transition: all 0.2s;
}

.btn-input-icon:hover {
    background: rgba(0, 0, 0, 0.05);
    color: #f5a623;
}

.btn-input-icon .fa-star-o {
    font-size: 1.1rem;
}

.btn-send {
    width: 36px;
    height: 36px;
    min-width: 36px;
    border-radius: 50%;
    padding: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
}

.btn-send i {
    margin: 0 !important;
    padding: 0;
    line-height: 1;
}

.btn-send:disabled {
    opacity: 0.5;
}

.typing-indicator {
    display: flex;
    align-items: center;
    padding: 10px 14px;
    background: #fff;
    border-radius: 12px;
    border: 1px solid #e8eaed;
}

.typing-indicator span {
    width: 6px;
    height: 6px;
    background: #5f6368;
    border-radius: 50%;
    margin-right: 4px;
    animation: typing 1.4s infinite ease-in-out both;
}

.typing-indicator span:nth-child(1) { animation-delay: -0.32s; }
.typing-indicator span:nth-child(2) { animation-delay: -0.16s; }
.typing-indicator span:nth-child(3) { animation-delay: 0; margin-right: 0; }

@keyframes typing {
    0%, 80%, 100% { transform: scale(0.6); opacity: 0.5; }
    40% { transform: scale(1); opacity: 1; }
}

.link-agente {
    color: rgb(36, 61, 75);
    text-decoration: none;
}

.link-agente:hover {
    text-decoration: underline;
}

.message-text code {
    background: rgba(0,0,0,0.05);
    padding: 2px 6px;
    border-radius: 4px;
    font-size: 0.85em;
}
</style>

<style>
    
    

.tira-sombra :deep(button.btn),
.tira-sombra :deep(button.btn.dropdown-toggle) {
    padding: 4px 8px;
    background: transparent !important;
    background-color: transparent !important;
    border: none !important;
    border-color: transparent !important;
    box-shadow: none !important;
    outline: none !important;
    color: #666;
}
.tira-sombra .btn,
.tira-sombra .dropdown-toggle,
.tira-sombra :deep(button.btn:hover),
.tira-sombra :deep(button.btn:focus),
.tira-sombra :deep(button.btn:active),
.tira-sombra :deep(button.btn.show),
.tira-sombra :deep(button.btn:focus-visible),
.tira-sombra :deep(button.btn.dropdown-toggle:hover),
.tira-sombra :deep(button.btn.dropdown-toggle:focus),
.tira-sombra :deep(button.btn.dropdown-toggle:active),
.tira-sombra :deep(button.btn.dropdown-toggle.show),
.tira-sombra :deep(button.btn.dropdown-toggle:focus-visible) {
    color: rgb(36, 61, 75);
    background: transparent !important;
    background-color: transparent !important;
    border: none !important;
    border-color: transparent !important;
    box-shadow: none !important;
    outline: none !important;
    
}

/* ========== MARKDOWN STYLES ========== */

/* Headers - tamanhos menores para contexto de chat */
.message-text .md-header {
    margin: 10px 0 6px 0;
    font-weight: 600;
    color: #243d4b;
    border-bottom: none;
    line-height: 1.3;
}
.message-text .md-header:first-child {
    margin-top: 0;
}
.message-text .md-h1 { font-size: 1.05em; }
.message-text .md-h2 { font-size: 1em; }
.message-text .md-h3 { font-size: 0.95em; }
.message-text .md-h4 { font-size: 0.9em; }

/* Parágrafos */
.message-text .md-paragraph {
    margin: 0 0 8px 0;
}
.message-text .md-paragraph:last-child {
    margin-bottom: 0;
}

/* Listas - garantir que fiquem dentro do balão */
.message-text .md-list,
.message-text .md-list-ordered {
    margin: 8px 0;
    padding: 0;
    padding-left: 20px;
    list-style-position: outside;
    overflow: visible;
}
.message-text .md-list li,
.message-text .md-list-ordered li {
    margin: 4px 0;
    padding: 0;
    line-height: 1.5;
}
.message-text .md-list {
    list-style-type: disc;
}
.message-text .md-list-ordered {
    list-style-type: decimal;
}
.md-list{
    margin-left: 20px !important;
}
/* Garantir que o container do texto acomode as listas */
.message-text {
    overflow: visible;
}

/* Tabelas */
.message-text .table-responsive {
    margin: 12px 0;
    overflow-x: auto;
}
.message-text .md-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 0.85em;
}
.message-text .md-table th,
.message-text .md-table td {
    padding: 8px 12px;
    border: 1px solid #dee2e6;
    text-align: left;
}
.message-text .md-table th {
    background: #f8f9fa;
    font-weight: 600;
}
.message-text .md-table tr:hover td {
    background: #f5f5f5;
}

/* Wrapper da tabela com botão de expandir */
.message-text .table-wrapper {
    position: relative;
    margin: 8px 0;
}
.message-text .table-actions {
    display: flex;
    justify-content: flex-end;
    margin-bottom: 4px;
}
.message-text .btn-expand-table {
    font-size: 0.75em;
    padding: 2px 8px;
}

/* Modal de tabela expandida */
.expanded-table-container {
    overflow-x: auto;
}
.expanded-table-container .md-table {
    width: 100%;
    border-collapse: collapse;
}
.expanded-table-container .md-table th,
.expanded-table-container .md-table td {
    padding: 10px 14px;
    border: 1px solid #dee2e6;
    text-align: left;
}
.expanded-table-container .md-table th {
    background: #f8f9fa;
    font-weight: 600;
}
.expanded-table-container .md-table tr:hover td {
    background: #f5f5f5;
}

/* Blocos de código */
.message-text .code-block {
    background: #1e1e1e;
    color: #d4d4d4;
    padding: 12px 16px;
    border-radius: 8px;
    margin: 12px 0;
    overflow-x: auto;
    font-family: 'Consolas', 'Monaco', monospace;
    font-size: 0.85em;
    line-height: 1.4;
}
.message-text .code-block code {
    background: transparent;
    padding: 0;
    color: inherit;
}
.message-text .inline-code {
    background: rgba(0,0,0,0.06);
    padding: 2px 6px;
    border-radius: 4px;
    font-family: 'Consolas', 'Monaco', monospace;
    font-size: 0.9em;
}

/* Fórmulas matemáticas */
.message-text .math-block {
    display: block;
    text-align: center;
    margin: 16px 0;
    padding: 12px;
    background: #f8f9fa;
    border-radius: 8px;
    font-family: 'Times New Roman', serif;
    font-size: 1.1em;
}
.message-text .math-inline {
    font-family: 'Times New Roman', serif;
    font-style: italic;
}
.message-text .formula-text {
    font-style: normal;
    font-family: inherit;
}
.message-text .formula-frac {
    display: inline-flex;
    flex-direction: column;
    align-items: center;
    vertical-align: middle;
    margin: 0 4px;
}
.message-text .frac-num,
.message-text .frac-den {
    padding: 2px 4px;
}
.message-text .frac-line {
    width: 100%;
    height: 1px;
    background: currentColor;
}

/* Outros elementos */
.message-text del {
    color: #999;
    text-decoration: line-through;
}
.message-text strong {
    font-weight: 600;
}
.message-text em {
    font-style: italic;
}
[id^="sidebar-chat-ia"] {
    background: #fff !important;
}

[id^="sidebar-chat-ia"] .b-sidebar-body {
    display: flex;
    flex-direction: column;
}

@media (max-width: 768px) {
    [id^="sidebar-chat-ia"],
    [id^="sidebar-chat-ia"].b-sidebar,
    .b-sidebar[id^="sidebar-chat-ia"] {
        width: 100% !important;
        max-width: 100vw !important;
        min-width: 100% !important;
    }
}

/* === MCP v2 - Confirmation buttons (Human-in-the-Loop) === */
.confirmation-buttons {
    display: flex;
    gap: 10px;
    margin-top: 12px;
    padding-top: 12px;
    border-top: 1px solid rgba(0,0,0,0.1);
}

.confirmation-buttons .btn {
    padding: 8px 16px;
    font-weight: 500;
    display: flex;
    align-items: center;
    gap: 6px;
}

.confirmation-buttons .btn-success {
    background: linear-gradient(135deg, #4caf50, #45a049);
    border: none;
    color: #fff;
}

.confirmation-buttons .btn-success:hover {
    background: linear-gradient(135deg, #45a049, #3d8b40);
}

.confirmation-buttons .btn-danger {
    background: linear-gradient(135deg, #f44336, #e53935);
    border: none;
    color: #fff;
}

.confirmation-buttons .btn-danger:hover {
    background: linear-gradient(135deg, #e53935, #d32f2f);
}

.confirmation-answered {
    margin-top: 8px;
    padding: 6px 10px;
    background: rgba(0,0,0,0.03);
    border-radius: 6px;
}

/* === MCP v2 - Selection buttons (Multiple results) === */
.selection-buttons {
    margin-top: 12px;
    padding-top: 12px;
    border-top: 1px solid rgba(0,0,0,0.1);
}

.selection-buttons .btn {
    display: flex;
    align-items: center;
    gap: 6px;
    text-align: left;
}

.selection-buttons .badge {
    min-width: 22px;
    height: 22px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
}

.selection-answered {
    margin-top: 8px;
    padding: 6px 10px;
    background: rgba(0,0,0,0.03);
    border-radius: 6px;
}

/* === Auto-scroll button === */
.scroll-to-bottom-btn {
    position: absolute;
    bottom: 20px;
    right: 20px;
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background: linear-gradient(135deg, #667eea, #764ba2);
    color: #fff;
    border: none;
    box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 16px;
    transition: all 0.3s ease;
    z-index: 10;
}

.scroll-to-bottom-btn:hover {
    transform: scale(1.1);
    box-shadow: 0 6px 16px rgba(102, 126, 234, 0.5);
}

.fade-enter-active, .fade-leave-active {
    transition: opacity 0.3s ease;
}
.fade-enter, .fade-leave-to {
    opacity: 0;
}

/* === Message tools clickable === */
.message-tools.clickable {
    cursor: pointer;
    padding: 4px 8px;
    border-radius: 4px;
    transition: background 0.2s;
}

.message-tools.clickable:hover {
    background: rgba(0,0,0,0.05);
}

.message-tools.clickable small {
    color: #667eea;
}

/* === Debug Modal === */
.debug-modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0,0,0,0.5);
    z-index: 9999;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 20px;
}

.debug-modal {
    background: #fff;
    border-radius: 12px;
    max-width: 800px;
    width: 100%;
    max-height: 80vh;
    display: flex;
    flex-direction: column;
    box-shadow: 0 20px 40px rgba(0,0,0,0.2);
}

.debug-modal-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px 20px;
    border-bottom: 1px solid #e9ecef;
    background: linear-gradient(135deg, #667eea, #764ba2);
    color: #fff;
    border-radius: 12px 12px 0 0;
}

.debug-modal-header h5 {
    margin: 0;
    font-size: 1rem;
}

.debug-modal-header .close-btn {
    background: rgba(255,255,255,0.2);
    border: none;
    color: #fff;
    width: 32px;
    height: 32px;
    border-radius: 50%;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
}

.debug-modal-header .close-btn:hover {
    background: rgba(255,255,255,0.3);
}

.debug-modal-body {
    padding: 20px;
    overflow-y: auto;
    flex: 1;
}

.debug-section {
    margin-bottom: 20px;
    padding-bottom: 16px;
    border-bottom: 1px solid #f0f0f0;
}

.debug-section:last-child {
    border-bottom: none;
    margin-bottom: 0;
}

.debug-section h6 {
    font-size: 0.85rem;
    font-weight: 600;
    color: #495057;
    margin-bottom: 12px;
    display: flex;
    align-items: center;
    gap: 6px;
}

.debug-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
    gap: 10px;
}

.debug-item {
    background: #f8f9fa;
    padding: 8px 12px;
    border-radius: 6px;
}

.debug-item .label {
    font-size: 0.75rem;
    color: #6c757d;
    display: block;
    margin-bottom: 2px;
}

.debug-item code {
    font-size: 0.8rem;
    color: #495057;
    background: transparent;
}

/* Tool Calls List */
.tool-calls-list {
    display: flex;
    flex-direction: column;
    gap: 10px;
}

.tool-call-item {
    background: #f8f9fa;
    border-radius: 8px;
    overflow: hidden;
}

.tool-call-item.tool-error {
    border-left: 3px solid #dc3545;
}

.tool-call-header {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 14px;
    cursor: pointer;
    transition: background 0.2s;
}

.tool-call-header:hover {
    background: #e9ecef;
}

.tool-expand-icon {
    color: #6c757d;
    font-size: 0.8rem;
}

.tool-num {
    width: 24px;
    height: 24px;
    border-radius: 50%;
    background: #667eea;
    color: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.75rem;
    font-weight: bold;
}

.tool-name {
    flex: 1;
    font-weight: 500;
    font-size: 0.85rem;
}

.tool-badges {
    display: flex;
    gap: 6px;
}

.tool-call-expanded {
    padding: 12px 14px;
    background: #fff;
    border-top: 1px solid #e9ecef;
}

.tool-section {
    margin-bottom: 12px;
}

.tool-section:last-child {
    margin-bottom: 0;
}

.tool-section-title {
    font-size: 0.75rem;
    font-weight: 600;
    color: #6c757d;
    margin-bottom: 6px;
    cursor: pointer;
}

.params-grid {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
}

.param-item {
    background: #e9ecef;
    padding: 4px 8px;
    border-radius: 4px;
    font-size: 0.75rem;
}

.param-key {
    color: #6c757d;
    margin-right: 4px;
}

.param-value {
    color: #495057;
    font-size: 0.75rem;
}

.tool-error-message {
    background: #fff3f3;
    color: #dc3545;
    padding: 8px 12px;
    border-radius: 6px;
    font-size: 0.8rem;
    margin-bottom: 12px;
}

.tool-response-debug,
.tool-response-container-debug {
    background: #1e1e1e;
    border-radius: 6px;
    padding: 12px;
    max-height: 300px;
    overflow: auto;
}

.raw-json {
    margin: 0;
    font-size: 0.75rem;
    color: #d4d4d4;
    white-space: pre-wrap;
    word-break: break-all;
}

.raw-json .json-key { color: #9cdcfe; }
.raw-json .json-string { color: #ce9178; }
.raw-json .json-number { color: #b5cea8; }
.raw-json .json-boolean { color: #569cd6; }
.raw-json .json-null { color: #569cd6; }

/* Thoughts List */
.thoughts-list {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.thought-item {
    display: flex;
    gap: 10px;
    align-items: flex-start;
}

.thought-num {
    width: 22px;
    height: 22px;
    border-radius: 50%;
    background: #e9ecef;
    color: #6c757d;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.7rem;
    font-weight: bold;
    flex-shrink: 0;
}

.thought-body {
    flex: 1;
    font-size: 0.8rem;
}

.thought-body strong {
    display: block;
    color: #495057;
    margin-bottom: 2px;
}

.thought-body p {
    margin: 0;
    color: #6c757d;
}

/* Stream Log */
.stream-log {
    background: #1e1e1e;
    border-radius: 6px;
    max-height: 250px;
    overflow-y: auto;
}

.log-row {
    border-bottom: 1px solid #333;
}

.log-row:last-child {
    border-bottom: none;
}

.log-header {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 12px;
    cursor: pointer;
    color: #d4d4d4;
    font-size: 0.75rem;
}

.log-header:hover {
    background: #2d2d2d;
}

.log-type {
    padding: 2px 6px;
    border-radius: 4px;
    font-size: 0.7rem;
    font-weight: bold;
}

.log-type.type-step { background: #4caf50; color: #fff; }
.log-type.type-thought { background: #2196f3; color: #fff; }
.log-type.type-response { background: #9c27b0; color: #fff; }
.log-type.type-done { background: #607d8b; color: #fff; }
.log-type.type-error { background: #f44336; color: #fff; }

.log-preview {
    color: #888;
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.log-json {
    margin: 0;
    padding: 8px 12px;
    background: #252525;
    font-size: 0.7rem;
    color: #d4d4d4;
    white-space: pre-wrap;
    word-break: break-all;
}

/* Tools badges fallback */
.tools-badges {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
}

/* Chat messages relative for scroll button */
.chat-messages {
    position: relative;
}
</style>
